import { Viewer, MODES } from "./viewer.js";
import { initTickets } from "./tickets.js";
import { annotateImage } from "./annotate.js";
import { initStudio } from "./studio.js";
import { initBoard } from "./board.js";
import { initReviews } from "./reviews.js";
import { initStructures, renderStructureRevisionTree, renderStructureReviewChoices } from "./buildings.js";
import { TerrainEditor } from "./terrainEditor.js";
import { viewerKeyboardShortcut } from "./terrainNavigation.js";
import { buildRoute, isUnmodifiedPrimaryLinkClick, parseRoute } from "./appRouter.js";
import { artifactLink, bindArtifactLightboxes, imagePreview, openArtifactLightbox } from "./artifact-links.js";
import { initMegastructureStudio } from "./megastructureStudio.js";

/** Same-origin API (the Worker serves this page). Dev identity goes in the
 * x-dev-user-email header; outside dev, Cloudflare Access injects identity. */

const $ = (sel) => document.querySelector(sel);

const emailInput = $("#user-email");
emailInput.value = localStorage.getItem("wl_email") ?? "peter.ericson@ideaworks.dk";
emailInput.addEventListener("change", () => localStorage.setItem("wl_email", emailInput.value));

const DEV_API_ORIGIN = "https://worldloom-api-dev.pg-ericson.workers.dev";
// Wrangler serves the SPA and API on the same local origin. Only a standalone
// frontend dev server opts into the deployed dev API.
const API_ORIGIN = /^(127\.0\.0\.1|localhost)$/.test(location.hostname) && ["3000", "5173"].includes(location.port)
  ? DEV_API_ORIGIN
  : "";
const apiUrl = (path) => (/^https?:\/\//.test(path) ? path : `${API_ORIGIN}${path}`);
const fileUrl = (key) => (/^https?:\/\//.test(key) ? key : `${API_ORIGIN}/files/${key}`);

async function api(path, opts = {}) {
  const res = await fetch(apiUrl(path), {
    ...opts,
    headers: {
      "content-type": "application/json",
      "x-dev-user-email": emailInput.value,
      ...(opts.headers ?? {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = typeof body.error === "string" ? body.error : body.error ? JSON.stringify(body.error) : `${path} → ${res.status}`;
    const issue = Array.isArray(body.issues) && body.issues[0]?.message ? body.issues[0].message : "";
    throw new Error(issue ? `${message}: ${issue}` : message);
  }
  return body;
}

async function apiForm(path, form) {
  const res = await fetch(apiUrl(path), {
    method: "POST",
    headers: { "x-dev-user-email": emailInput.value },
    body: form,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error ? JSON.stringify(body.error) : `${path} -> ${res.status}`);
  return body;
}

function toast(msg, isError = false) {
  const el = $("#toast") ?? document.body.appendChild(Object.assign(document.createElement("div"), { id: "toast" }));
  el.textContent = msg;
  el.style.borderColor = isError ? "var(--err)" : "var(--line)";
  el.classList.add("show");
  clearTimeout(el._t);
  el._t = setTimeout(() => el.classList.remove("show"), 3500);
}

const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
const json = (o) => `<pre class="json">${esc(JSON.stringify(o, null, 2))}</pre>`;
const badge = (s) => `<span class="badge ${esc(s)}">${esc(s)}</span>`;
const when = (t) => (t ? new Date(t).toLocaleString() : "—");
const cm = (v) => `${v.toFixed(1)}`;

/* ---------- dictation ---------- */

const dictateFile = $("#dictate-file");
const dictateStatus = $("#dictate-status");
let lastEditable = null;
let recorder = null;
let recordChunks = [];
let recordTarget = null;
let recordButton = null;

function isEditable(el) {
  return el && (el.matches?.("textarea,input[type='text'],input[type='search'],input:not([type])") || el.isContentEditable);
}

function canDictateInto(el) {
  return isEditable(el) && el.type !== "search" && el !== emailInput && !el.readOnly && !el.disabled && !el.closest(".no-dictate");
}

document.addEventListener("focusin", (e) => {
  if (canDictateInto(e.target)) lastEditable = e.target;
});

function setDictateStatus(text) {
  dictateStatus.textContent = text || "";
}

function insertTranscript(text, requestedTarget = null) {
  const active = canDictateInto(document.activeElement) ? document.activeElement : null;
  const target = requestedTarget && canDictateInto(requestedTarget) ? requestedTarget : active || lastEditable;
  if (!target) {
    navigator.clipboard?.writeText(text).catch(() => {});
    toast("transcript copied; focus a field before dictating to insert it");
    return;
  }
  if (target.isContentEditable) {
    target.focus();
    document.execCommand("insertText", false, text);
  } else {
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? start;
    const before = target.value.slice(0, start);
    const after = target.value.slice(end);
    const spacer = before && !/\s$/.test(before) ? " " : "";
    target.value = `${before}${spacer}${text}${after}`;
    const caret = before.length + spacer.length + text.length;
    target.setSelectionRange?.(caret, caret);
    target.dispatchEvent(new Event("input", { bubbles: true }));
    target.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

async function transcribeFiles(files, target = null) {
  const list = [...files].filter(Boolean);
  if (!list.length) return;
  const form = new FormData();
  for (const file of list) form.append("files", file, file.name);
  form.append("model_id", "scribe_v2");
  setDictateStatus(`Transcribing ${list.length} file${list.length === 1 ? "" : "s"}...`);
  try {
    const result = await apiForm("/transcriptions/bulk", form);
    if (result.text) insertTranscript(result.text, target);
    const failed = (result.results || []).filter((item) => !item.ok);
    setDictateStatus(failed.length ? `Transcribed with ${failed.length} failure${failed.length === 1 ? "" : "s"}` : "Transcript inserted");
    toast(failed.length ? "transcription completed with some failures" : "transcript inserted", Boolean(failed.length));
  } catch (err) {
    setDictateStatus(`Transcription failed: ${String(err)}`);
    toast(`Transcription failed: ${String(err)}`, true);
  }
}

async function transcribeTerrainCommand(file) {
  const form = new FormData();
  form.append("files", file, file.name);
  form.append("model_id", "scribe_v2");
  const result = await apiForm("/transcriptions/bulk", form);
  return result.text ?? "";
}

dictateFile.onchange = () => {
  transcribeFiles(dictateFile.files).finally(() => {
    dictateFile.value = "";
  });
};

function finishRecordingUi() {
  recordTarget?.classList.remove("dictating-field");
  recordButton?.classList.remove("on");
  recordButton = null;
  recordTarget = null;
}

async function toggleFieldRecording(target, button) {
  if (recorder?.state === "recording") {
    recordButton?.classList.remove("on");
    if (button !== recordButton) button.classList.remove("on");
    recorder.stop();
    setDictateStatus("Preparing audio...");
    return;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    recordChunks = [];
    recordTarget = target;
    recordButton = button;
    recorder = new MediaRecorder(stream);
    recorder.ondataavailable = (e) => {
      if (e.data.size) recordChunks.push(e.data);
    };
    recorder.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      const blob = new Blob(recordChunks, { type: recorder.mimeType || "audio/webm" });
      const file = new File([blob], `dictation-${Date.now()}.webm`, { type: blob.type });
      const targetAtStop = recordTarget;
      transcribeFiles([file], targetAtStop).finally(finishRecordingUi);
    };
    recorder.start();
    target.classList.add("dictating-field");
    button.classList.add("on");
    setDictateStatus("Recording...");
  } catch (err) {
    finishRecordingUi();
    setDictateStatus(`Mic unavailable: ${String(err)}`);
    toast(`Mic unavailable: ${String(err)}`, true);
  }
}

function enhanceDictationFields(root = document) {
  root.querySelectorAll?.("textarea,input[type='text'],input[type='search'],input:not([type])").forEach((el) => {
    if (!canDictateInto(el) || el.dataset.dictateReady) return;
    el.dataset.dictateReady = "1";
    el.classList.add("has-dictate");
    const host = el.parentElement;
    if (!host) return;
    host.classList.add("dictate-host");
    const mic = document.createElement("button");
    mic.type = "button";
    mic.className = "field-mic";
    mic.textContent = "mic";
    mic.title = "Dictate into this field";
    mic.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      lastEditable = el;
      el.focus();
      toggleFieldRecording(el, mic);
    };
    host.appendChild(mic);
  });
}

const dictateObserver = new MutationObserver(() => requestAnimationFrame(() => enhanceDictationFields()));
dictateObserver.observe(document.body, { childList: true, subtree: true });
enhanceDictationFields();

/* ---------- stage ---------- */

const viewer = new Viewer($("#viewport"), { fileUrl });
let currentStats = null;
let currentDetail = null;
let currentTab = "pipeline";
let activeAssetReview = null;
let assetEvidenceLoaded = false;
let assetEvidenceError = "";

const modeBox = $("#mode-buttons");
for (const [id, label] of MODES) {
  const b = document.createElement("button");
  b.textContent = label;
  b.dataset.mode = id;
  if (id === "shaded") b.classList.add("on");
  b.onclick = () => {
    viewer.setMode(id);
    modeBox.querySelectorAll("button").forEach((x) => x.classList.toggle("on", x === b));
  };
  modeBox.appendChild(b);
}
const advancedViewerBtn = document.createElement("button");
advancedViewerBtn.id = "advanced-viewer-btn";
advancedViewerBtn.type = "button";
advancedViewerBtn.textContent = "More view tools";
advancedViewerBtn.setAttribute("aria-expanded", "false");
advancedViewerBtn.onclick = () => {
  const open = document.body.classList.toggle("viewer-tools-open");
  advancedViewerBtn.textContent = open ? "Fewer view tools" : "More view tools";
  advancedViewerBtn.setAttribute("aria-expanded", String(open));
};
$("#toolbar").appendChild(advancedViewerBtn);

document.querySelectorAll("[data-toggle]").forEach((b) => {
  b.onclick = () => {
    const on = !b.classList.contains("on");
    b.classList.toggle("on", on);
    viewer.setToggle(b.dataset.toggle, on);
  };
});
$("#exposure").oninput = (e) => viewer.setExposure(Number(e.target.value));
const flyBtn = $("#fly-btn");
const flyHint = $("#fly-hint");
const terrainBtn = document.createElement("button");
terrainBtn.id = "terrain-btn";
terrainBtn.type = "button";
terrainBtn.textContent = "terrain lab";
terrainBtn.title = "Open the procedural terrain editor";
flyBtn.before(terrainBtn);
viewer.onFlyChange = ({ enabled, active, speed }) => {
  flyBtn.classList.toggle("on", enabled);
  flyBtn.setAttribute("aria-pressed", String(enabled));
  $("#viewport").classList.toggle("fly-mode", enabled);
  $("#viewport").classList.toggle("fly-active", active);
  flyHint.hidden = !enabled;
  flyHint.textContent = active
    ? `W/S follow view · A/D strafe · ${(speed * viewer.fly.travelMultiplier).toFixed(1)} m/s · Shift normal ${speed.toFixed(1)} m/s · Space up · Ctrl down · Esc release`
    : "Click the world to capture the mouse and continue flying";
};
flyBtn.onclick = () => viewer.setFlyMode(!flyBtn.classList.contains("on"));
let terrainEditor = null;
terrainBtn.onclick = () => {
  if (!terrainEditor) return;
  if (terrainEditor.active) terrainEditor.deactivate();
  else {
    terrainEditor.activate();
    terrainBtn.classList.add("on");
    terrainBtn.textContent = "close terrain";
    if (currentWorldId) navigateRoute("world", { worldId: currentWorldId, worldName: worldNames.get(currentWorldId), state: { ...activeRoute.state, pane: "terrain" } }, { focus: false });
  }
};
$("#frame-btn").onclick = () => viewer.frame();
$("#shot-btn").onclick = () => {
  const a = document.createElement("a");
  a.href = viewer.screenshot();
  a.download = `${currentRadialPreview?.name ?? currentDetail?.asset?.displayName ?? "asset"}.png`;
  a.click();
};

/* ---------- spatial feedback: draw on the 3D view, file a critique ---------- */

const feedbackBtn = document.createElement("button");
feedbackBtn.id = "feedback-btn";
feedbackBtn.textContent = "✏️ feedback";
feedbackBtn.title = "Snapshot the view, draw on it, and file feedback scoped to a cell or asset";
$("#shot-btn").after(feedbackBtn);
feedbackBtn.onclick = () => {
  let scopeOptions;
  if (currentView === "level" && worldData?.layout) {
    scopeOptions = worldData.layout.doc.cells.map((c) => ({ value: `cell:${c.cellId}`, label: `${c.name} · section ${c.cellId}` }));
  } else if (currentView === "assets" && currentDetail?.asset) {
    scopeOptions = [{ value: `asset:${currentDetail.asset.assetUid}`, label: currentDetail.asset.displayName }];
  } else {
    return toast("open a world or select an asset first — feedback needs something to point at", true);
  }
  annotateImage({
    src: viewer.screenshot(),
    note: true,
    scopeOptions,
    toast,
    onSave: async ({ blob, note, scopeValue, strokeBounds, close }) => {
      const projectId = studio.getProjectId();
      if (!projectId) return toast("select a project before filing feedback", true);
      const key = `projects/${projectId}/uploads/feedback/${crypto.randomUUID()}.png`;
      const [kind, id] = scopeValue.split(/:(.+)/);
      const scope = kind === "cell" ? { kind: "cell", cellId: id } : { kind: "asset", assetUid: id };

      // asset feedback with a drawing: resolve WHERE the marks landed on the
      // mesh (raycast through the unchanged camera) → decal anchor
      let decalAnchor;
      if (kind === "asset" && strokeBounds) {
        const hit = viewer.raycastModel(strokeBounds.cx, strokeBounds.cy);
        if (hit) {
          const edge = viewer.raycastModel(strokeBounds.cx + strokeBounds.w / 2, strokeBounds.cy);
          const dist = edge ? Math.hypot(...hit.point.map((v, i) => v - edge.point[i])) * 2 : 0;
          const maxDim = Math.max(currentStats?.sizeCm.x ?? 100, currentStats?.sizeCm.z ?? 100) / 100;
          decalAnchor = {
            point: hit.point,
            normal: hit.normal,
            sizeM: Math.min(Math.max(dist || maxDim * 0.25, 0.01), maxDim * 0.9),
          };
        }
      }

      try {
        await api(`/files/${key}`, { method: "PUT", body: blob, headers: { "content-type": "image/png" } });
        const { ticket } = await api("/critique", {
          method: "POST",
          body: JSON.stringify({
            projectId,
            scope,
            body: note,
            author: emailInput.value,
            attachments: [key],
            ...(decalAnchor ? { decalAnchor } : {}),
            ...(currentView === "level" && currentWorldId ? { worldId: currentWorldId } : {}),
          }),
        });
        toast(`feedback filed → revise ticket ${ticket.id.slice(0, 8)}`);
        close();
        // show the feedback immediately where the user is looking —
        // without reloading the mesh (their camera stays put)
        if (currentView === "assets" && currentDetail?.asset) {
          currentDetail = await api(`/catalog/${currentDetail.asset.assetUid}/detail`);
          renderDetail();
        } else if (currentView === "level") {
          await loadWorld(); // pins refresh
          showFeedbackFlow(ticket.id); // right bar shows the new feedback's flow
        }
      } catch (err) {
        toast(String(err), true);
      }
    },
  });
};
addEventListener("keydown", (e) => {
  const shortcut = viewerKeyboardShortcut(e, { toolOwnsKeyboard: Boolean(terrainEditor?.active) });
  if (shortcut === "fly") flyBtn.click();
  if (shortcut === "grid") $('[data-toggle="grid"]').click();
  if (shortcut === "bbox") $('[data-toggle="bbox"]').click();
  if (shortcut === "turntable") $('[data-toggle="turntable"]').click();
});

// local .glb drop for quick previews
const vp = $("#viewport");
vp.addEventListener("dragover", (e) => e.preventDefault());
vp.addEventListener("drop", async (e) => {
  e.preventDefault();
  const f = e.dataTransfer.files?.[0];
  if (!f?.name.endsWith(".glb")) return toast("drop a .glb file", true);
  $("#drop-hint").style.display = "none";
  currentStats = await viewer.load(URL.createObjectURL(f));
  currentDetail = null;
  renderStats();
  renderDetail();
});

function renderStats() {
  const bar = $("#statsbar");
  if (!currentStats) return (bar.innerHTML = "");
  const s = currentStats;
  const spec = currentDetail?.asset?.doc?.bounds_cm;
  let dims = `<span>size <b>${cm(s.sizeCm.x)} × ${cm(s.sizeCm.z)} × ${cm(s.sizeCm.y)}</b> cm (W×D×H)</span>`;
  if (spec?.extent) {
    const sx = spec.extent.x * 2, sy = spec.extent.y * 2, sz = spec.extent.z * 2;
    const off = Math.max(Math.abs(s.sizeCm.x - sx), Math.abs(s.sizeCm.z - sy), Math.abs(s.sizeCm.y - sz));
    dims += `<span class="${off > Math.max(sx, sy, sz) * 0.25 ? "dim-warn" : ""}">spec <b>${cm(sx)} × ${cm(sy)} × ${cm(sz)}</b> cm</span>`;
  }
  bar.innerHTML = `
    <span>tris <b>${s.triangles.toLocaleString()}</b></span>
    <span>verts <b>${s.vertices.toLocaleString()}</b></span>
    <span>materials <b>${s.materials}</b></span>
    <span>textures <b>${s.textures}</b></span>
    ${s.previewKind === "stacked_megastructure_visualization" ? `<span>live instances <b>${s.instances}</b></span><span>height <b>${(s.overallHeightM / 1000).toFixed(1)} km</b></span>` : s.sourceMeshCount ? `<span>source mesh <b>${s.sourceMeshCount}</b></span><span>${s.previewKind === "source_segment" ? "preview pieces" : "instances"} <b>${s.instances}</b></span>${s.previewKind === "source_segment" ? "" : `<span>seam <b>${s.maximumSeamErrorCm < 0.001 ? "< 0.001" : s.maximumSeamErrorCm.toFixed(3)} cm</b></span>`}` : ""}
    ${dims}`;
}

/* ---------- asset list ---------- */

/* ---------- view switch: Assets | World ---------- */

let currentView = "studio";
let worldData = null;
let currentRadialPreview = null;
let currentStructurePreviewContext = null;
let currentWorldId = localStorage.getItem("wl_world") || "";
let worldNames = new Map();
let activeRoute = parseRoute(location);
let applyingRoute = false;
let pendingRoute = activeRoute;
const filterBoxEl = $("#status-filters");

// level picker — each Level is a distinct playable chunk (café courtyard,
// dust2 A site, …) with its own layout/blockout/manifests via GET /world/:id
const worldPicker = document.createElement("select");
worldPicker.id = "world-picker";
worldPicker.style.display = "none";
worldPicker.onchange = () => {
  if (terrainEditor?.active) terrainEditor.deactivate({ restore: false });
  terrainBtn.classList.remove("on");
  terrainBtn.textContent = "terrain lab";
  currentWorldId = worldPicker.value;
  localStorage.setItem("wl_world", currentWorldId);
  navigateRoute("world", { worldId: currentWorldId, worldName: worldNames.get(currentWorldId) });
};
filterBoxEl.parentElement.insertBefore(worldPicker, filterBoxEl);

/* ---------- top-level views: Requests | Tickets | Inbox | Level | Assets ---------- */

let ticketsView;
function openTicket(ticketId, ticketName = "") {
  navigateRoute("ticket", { ticketId, ticketName });
}
ticketsView = initTickets({
  api, toast, esc, fileUrl,
  hrefFor: descriptorHref,
  onNavigate: navigateDescriptor,
  onRouteStateChange: replaceDescriptorRoute,
});
const panelEl = $("#panel");
const mobileInspectorMedia = window.matchMedia("(max-width: 760px)");
const closeMobileInspector = () => $("#detail").classList.remove("mobile-open");
const openMobileInspector = () => {
  if (mobileInspectorMedia.matches && (currentView === "level" || currentView === "assets" || currentView === "structure-preview")) {
    $("#detail").classList.add("mobile-open");
  }
};
$("#mobile-detail-close").onclick = closeMobileInspector;
$("#review-return").onclick = openMobileInspector;
const studio = initStudio({ api, toast, esc, navigate: navigateView, hrefFor: (view) => projectRoute(VIEW_ROUTES[view] || "studio") });
const megastructureStudio = initMegastructureStudio({
  viewer,
  api,
  toast,
  esc,
  getProjectId: () => studio.getProjectId(),
  setStats: (stats) => { currentStats = stats; renderStats(); },
  onRevisionChange: (revisionId, { replace = false } = {}) => navigateRoute("visualizer", { state: { revision: revisionId } }, { replace, focus: false }),
});
const structures = initStructures({
  api,
  toast,
  esc,
  fileUrl,
  getProjectId: () => studio.getProjectId(),
  navigate: navigateView,
  onNavigate: navigateRoute,
  openRadialPreview: (structure) => {
    currentRadialPreview = structure;
    navigateRoute("preview", {
      structureId: structure.sourceStructureId || structure.id,
      structureName: structure.name,
      state: { revision: structures.getRouteState().revisionId || "" },
    });
  },
  onRouteStateChange: (state) => {
    if (applyingRoute || currentView !== "buildings") return;
    const structureId = state.structureId || "";
    const conversationId = state.workspace === "session" ? state.conversationId || "" : "";
    const route = conversationId ? "structureSession" : state.workspace === "session" && state.pane === "chat" ? "structureChange" : structureId ? "structure" : "structures";
    navigateRoute(route, {
      structureId,
      conversationId,
      conversationName: state.conversationName || "Structure discussion",
      state: route === "structureChange"
        ? { successor: state.successorRevisionId || "", retry: state.retryRevisionId || "" }
        : { revision: state.revisionId || "", pane: state.pane || "", tab: state.workspace || "", successor: state.successorRevisionId || "", retry: state.retryRevisionId || "" },
    }, { replace: true, focus: false });
  },
  routeHref: descriptorHref,
});
const board = initBoard({
  api,
  toast,
  esc,
  openTicket,
  routeHref: (ticket) => projectRoute("ticket", { ticketId: ticket.id, ticketName: ticket.title || ticket.type }),
  onRouteStateChange: (state) => {
    if (applyingRoute || currentView !== "tickets") return;
    history.replaceState({}, "", projectRoute("board", { state }));
    activeRoute = parseRoute(location);
    updateNavigation();
  },
});
const setNeedsCount = (count) => {
  const badge = $("#needs-you-count");
  badge.hidden = count === 0;
  badge.textContent = String(count);
  $("#needs-you-btn").classList.toggle("has-items", count > 0);
};
const reviews = initReviews({
  api,
  toast,
  esc,
  fileUrl,
  openTicket,
  openWorld: (worldId) => {
    currentWorldId = worldId;
    localStorage.setItem("wl_world", worldId);
    navigateRoute("world", { worldId, worldName: worldNames.get(worldId) });
  },
  openAssetReview: async (review) => {
    activeAssetReview = review;
    history.replaceState({}, "", projectRoute("ticketReview", { ticketId: review.ticket.id, ticketName: review.ticket.title || review.ticket.type, state: { tab: "review" } }));
    activeRoute = parseRoute(location);
    await switchView("assets");
    await selectAsset(review.assetUid, { preserveReview: true });
    currentTab = "review";
    updateDetailTabs();
    renderDetail();
    updateNavigation();
  },
  onCountChange: setNeedsCount,
  hrefFor: descriptorHref,
  onNavigate: navigateDescriptor,
  onRouteStateChange: replaceDescriptorRoute,
});

const VIEW_ROUTES = {
  studio: "studio",
  level: "worlds",
  buildings: "structures",
  tickets: "board",
  "ticket-detail": "tickets",
  assets: "assets",
  visualizer: "visualizer",
  inbox: "reviews",
};

function projectRoute(route, options = {}) {
  const project = studio.getProject();
  const projectId = studio.getProjectId();
  if (!projectId) return "/";
  return buildRoute({
    projectId,
    projectName: project?.name || project?.title || "project",
    route,
    ...options,
  });
}

function descriptorHref(descriptor) {
  const { route, ...options } = descriptor;
  return projectRoute(route, options);
}

function navigateDescriptor(descriptor) {
  const { route, ...options } = descriptor;
  return navigateRoute(route, options);
}

function replaceDescriptorRoute(descriptor) {
  const href = descriptorHref(descriptor);
  history.replaceState({}, "", href);
  activeRoute = parseRoute(location);
  pendingRoute = activeRoute;
  updateNavigation();
}

function navigateRoute(route, options = {}, { replace = false, focus = true } = {}) {
  const href = projectRoute(route, options);
  history[replace ? "replaceState" : "pushState"]({}, "", href);
  return applyRoute(parseRoute(location), { focus });
}

function navigateView(view, options = {}) {
  const route = VIEW_ROUTES[view] || "studio";
  return navigateRoute(route, options);
}

function currentViewRoute() {
  if (currentView === "level") return currentWorldId ? { route: "world", worldId: currentWorldId } : { route: "worlds" };
  if (currentView === "assets" && currentDetail?.asset?.assetUid) return { route: "asset", assetUid: currentDetail.asset.assetUid, assetName: currentDetail.asset.displayName, state: { tab: currentTab } };
  if (currentView === "visualizer") return { route: "visualizer", state: { revision: activeRoute.state?.revision || "" } };
  if (currentView === "buildings") {
    const state = structures.getRouteState();
    if (state.workspace === "session" && state.conversationId) {
      return { route: "structureSession", conversationId: state.conversationId, conversationName: state.conversationName || "Structure discussion", state: { pane: state.pane, tab: "session" } };
    }
    if (state.workspace === "session" && state.pane === "chat" && state.structureId) {
      return { route: "structureChange", structureId: state.structureId, state: { successor: state.successorRevisionId, retry: state.retryRevisionId } };
    }
    return state.structureId
      ? { route: "structure", structureId: state.structureId, state: { revision: state.revisionId, pane: state.pane, tab: state.workspace, successor: state.successorRevisionId, retry: state.retryRevisionId } }
      : { route: "structures", state: { revision: state.revisionId, pane: state.pane, tab: state.workspace, successor: state.successorRevisionId, retry: state.retryRevisionId } };
  }
  return { route: VIEW_ROUTES[currentView] || "studio" };
}

function updateNavigation() {
  const routeByView = {
    studio: ["studio", {}],
    level: ["worlds", {}],
    buildings: ["structures", {}],
    tickets: ["board", {}],
    assets: ["assets", {}],
    visualizer: ["visualizer", {}],
    inbox: ["reviews", {}],
  };
  document.querySelectorAll("[data-view]").forEach((link) => {
    const [route, options] = routeByView[link.dataset.view] || ["studio", {}];
    link.href = projectRoute(route, options);
    const navigationView = currentView === "structure-preview" ? "buildings" : currentView;
    const active = link.dataset.view === navigationView;
    link.classList.toggle("on", active);
    if (active) link.setAttribute("aria-current", "page");
    else link.removeAttribute("aria-current");
  });
  const names = {
    studio: "Studio", worlds: "Worlds", world: "World", section: "World section", feedback: "Feedback",
    structures: "Structures", structure: "Structure", structureChange: "Structure change chat", structureSession: "Structure discussion", preview: "Three.js structure preview", visualizer: "Live world visualizer", board: "Board",
    tickets: "Tickets", ticket: "Ticket", assets: "Assets", asset: "Asset", reviews: "Needs you",
    ticketReview: "Review", revisionReview: "Revision review",
  };
  const project = studio.getProject();
  document.title = `${names[activeRoute.route] || "Studio"} · ${project?.name || project?.title || "Worldloom"}`;
}

async function applyRoute(route, { focus = false } = {}) {
  applyingRoute = true;
  pendingRoute = route;
  try {
    if (route.projectId && route.projectId !== studio.getProjectId()) await studio.refresh(route.projectId);
    if (route.notFound) {
      toast("That address is not available. Opened the project Studio instead.", true);
      route = { ...route, route: "studio" };
    }
    activeRoute = route;
    statusFilter = route.state?.status || "";
    assetQuery = route.state?.query || "";
    assetSearch.value = assetQuery;
    filterBox.querySelectorAll("button").forEach((button) => button.classList.toggle("on", button.textContent === (statusFilter || "all")));
    currentTab = route.state?.tab || (activeAssetReview ? "review" : "pipeline");
    updateNavigation();

    if (["worlds", "world", "section", "feedback"].includes(route.route)) {
      if (route.worldId) {
        currentWorldId = route.worldId;
        localStorage.setItem("wl_world", currentWorldId);
      }
      await switchView("level");
      if (route.route === "section") showWorldSection(route.sectionId);
      if (route.route === "feedback") await showFeedbackFlow(route.ticketId);
      if (route.state?.pane === "terrain" && !terrainEditor.active) {
        terrainEditor.activate();
        terrainBtn.classList.add("on");
        terrainBtn.textContent = "close terrain";
      }
    } else if (["structures", "structure", "structureChange", "structureSession"].includes(route.route)) {
      await switchView("buildings");
      await structures.applyRouteState({
        workspace: route.route === "structureSession" || route.route === "structureChange" || route.state?.tab === "session" ? "session" : "atlas",
        revisionId: route.state?.revision || undefined,
        structureId: route.structureId || "",
        conversationId: route.conversationId || undefined,
        pane: route.route === "structureChange" ? "chat" : route.state?.pane ?? "",
        successorRevisionId: route.state?.successor ?? "",
        retryRevisionId: route.state?.retry ?? "",
      });
      const structureRouteState = structures.getRouteState();
      if (structureRouteState.workspace === "session" && structureRouteState.conversationId) {
        replaceDescriptorRoute({
          route: "structureSession",
          conversationId: structureRouteState.conversationId,
          conversationName: structureRouteState.conversationName || "Structure discussion",
          state: { pane: structureRouteState.pane || "conversation", tab: "session" },
        });
      } else if (route.route === "structure" || route.route === "structureChange") {
        const selectedStructure = await structures.resolveStructure(route.structureId, { revisionId: route.state?.revision || "" });
        if (selectedStructure) replaceDescriptorRoute({
          route: route.route,
          structureId: selectedStructure.id,
          structureName: selectedStructure.name,
          state: {
            ...(route.route === "structure" ? route.state : {}),
            ...(route.route === "structure" ? { pane: structureRouteState.pane || "", tab: structureRouteState.workspace || "" } : {}),
            successor: structureRouteState.successorRevisionId || "",
            retry: structureRouteState.retryRevisionId || "",
          },
        });
      }
    } else if (route.route === "preview") {
      const previewContext = await structures.resolveStructurePreview(route.structureId, {
        revisionId: route.state?.revision || "",
        source: route.state?.version === "source",
      });
      if (!previewContext) {
        toast("This structure preview is not available in the selected revision.", true);
        await switchView("buildings");
      } else {
        currentStructurePreviewContext = previewContext;
        currentRadialPreview = previewContext.structure;
        await switchView("structure-preview");
      }
    } else if (route.route === "visualizer") {
      await switchView("visualizer");
      await megastructureStudio.activate({ revisionId: route.state?.revision || "" });
    } else if (route.route === "board") {
      await switchView("tickets");
      await board.applyRouteState(route.state);
    } else if (route.route === "tickets") {
      await switchView("ticket-detail");
      await ticketsView.applyRouteState({ filter: route.state?.filter });
    } else if (route.route === "ticket") {
      await switchView("ticket-detail");
      await ticketsView.applyRouteState({ filter: route.state?.filter, ticketId: route.ticketId });
      replaceDescriptorRoute(ticketsView.getRouteDescriptor());
    } else if (route.route === "asset" || route.route === "assets") {
      await switchView("assets");
    } else if (["reviews", "ticketReview", "revisionReview"].includes(route.route)) {
      await switchView("inbox");
      await reviews.applyRouteState({
        filter: route.state?.filter,
        ticketId: route.ticketId,
        revisionId: route.revisionId,
      });
      if (currentView === "inbox") replaceDescriptorRoute(reviews.getRouteDescriptor());
    } else {
      await switchView("studio");
    }

    if (route.legacy && route.assetUid && studio.getProjectId()) {
      history.replaceState({}, "", projectRoute("asset", { assetUid: route.assetUid }));
      activeRoute = parseRoute(location);
    } else if (route.notFound && studio.getProjectId()) {
      history.replaceState({}, "", projectRoute("studio"));
      activeRoute = parseRoute(location);
    }
    updateNavigation();
    if (focus) $("#layout")?.focus({ preventScroll: true });
  } finally {
    applyingRoute = false;
  }
}

terrainEditor = new TerrainEditor({
  viewer,
  viewport: $("#viewport"),
  sidebar: $("#sidebar"),
  detail: $("#detail"),
  toast,
  esc,
  transcribe: transcribeTerrainCommand,
  getWorldId: () => currentWorldId,
  getWorldData: () => worldData,
  getProjectId: () => studio.getProjectId(),
  api,
  onExit: () => {
    terrainBtn.classList.remove("on");
    terrainBtn.textContent = "terrain lab";
    if (!applyingRoute && currentView === "level" && currentWorldId) {
      const state = { ...activeRoute.state };
      delete state.pane;
      navigateRoute("world", { worldId: currentWorldId, worldName: worldNames.get(currentWorldId), state }, { replace: true, focus: false });
    } else if (!applyingRoute && currentView === "level") {
      loadWorld().catch((error) => toast(String(error), true));
    }
  },
});

addEventListener("worldloom:project-change", () => {
  currentWorldId = "";
  localStorage.removeItem("wl_world");
  structures.projectChanged();
  reviews.refreshCount();
  if (!applyingRoute) {
    const next = currentViewRoute();
    navigateRoute(next.route, next, { replace: true, focus: false });
    return;
  }
  if (currentView === "level") loadWorldList().then(loadWorld).catch((err) => toast(String(err), true));
  else if (currentView === "assets") loadAssets().catch((err) => toast(String(err), true));
  else if (currentView === "tickets") board.render();
  else if (currentView === "inbox") reviews.render();
});
addEventListener("worldloom:artifact-reviewed", () => {
  studio.refresh().catch((err) => toast(String(err), true));
  if (currentView === "buildings") structures.refresh({ quiet: true });
  if (currentView === "level") loadWorldList().then(loadWorld).catch((err) => toast(String(err), true));
});

function renderRadialStructurePreview() {
  const structure = currentRadialPreview;
  if (!structure) return switchView("buildings");
  const previewContext = currentStructurePreviewContext || { structure, revision: null, versions: [], tickets: [] };
  const lineageStructureId = previewContext.lineageStructureId || structure.id;
  const revision = previewContext.revision;
  const versions = previewContext.versions || [];
  const activity = previewContext.sourceTicket || previewContext.tickets?.[0] || null;
  const segment = structure.segment || {};
  const longitudinalOpening = structure.longitudinalOpening || null;
  const roadLayout = longitudinalOpening?.roadLayout || null;
  const assembly = structure.assembly || {};
  const unreal = structure.unreal || {};
  const materials = unreal.materials || {};
  const vertices = Object.entries(segment.localTopVerticesM || {});
  const selectedStatus = String(revision?.status || "").toLowerCase();
  const selectedDecision = previewContext.latestDecision;
  const selectedVersionState = revision
    ? { revision: revision.id, version: `R${String(revision.revisionNumber || 0).padStart(2, "0")}` }
    : { version: "source" };
  const revisionState = selectedVersionState;
  const backRoute = structure.catalogSource ? "structures" : "structure";
  const dossierHref = structure.catalogSource
    ? projectRoute("structures", { state: revisionState })
    : projectRoute("structure", { structureId: structure.id, structureName: structure.name, state: revisionState });
  const proposalNeedsReview = Boolean(revision && ["review", "stale"].includes(selectedStatus));
  const proposalStopped = selectedStatus === "rejected" && selectedDecision?.decision === "reject";
  const proposalHref = proposalNeedsReview
    ? projectRoute("revisionReview", { revisionId: revision.id, revisionName: `${structure.name} R${String(revision.revisionNumber || 0).padStart(2, "0")}` })
    : proposalStopped
      ? projectRoute("structure", { structureId: structure.id, structureName: structure.name })
      : projectRoute("structureChange", {
        structureId: structure.id,
        structureName: structure.name,
        state: {
          ...revisionState,
          ...(selectedStatus === "rejected" && selectedDecision?.decision === "change_requested" ? { successor: revision.id } : {}),
        },
      });
  const proposalLabel = proposalNeedsReview
    ? `Review R${String(revision.revisionNumber || 0).padStart(2, "0")} to choose a path`
    : proposalStopped ? "Return to the current baseline" : "Open change chat";
  const specificationHref = projectRoute("preview", { structureId: lineageStructureId, structureName: structure.name, state: { ...revisionState, pane: "spec" } });
  if (activeRoute.route === "preview") {
    history.replaceState({}, "", projectRoute("preview", { structureId: lineageStructureId, structureName: structure.name, state: { ...activeRoute.state, ...selectedVersionState } }));
    activeRoute = parseRoute(location);
    pendingRoute = activeRoute;
  }

  const versionLabel = revision ? `R${String(revision.revisionNumber || 0).padStart(2, "0")}` : "SOURCE";
  const versionStatus = revision ? String(revision.status || "candidate").replace(/[_-]+/g, " ") : "repository specification";
  const versionLinks = [
    `<a href="${esc(projectRoute("preview", { structureId: lineageStructureId, structureName: "City Cordon Segment A", state: { version: "source" } }))}" class="${revision ? "" : "on"}" data-preview-version="source" aria-current="${revision ? "false" : "page"}"><span>SOURCE</span><b>Repository spec</b><small>Immutable template</small></a>`,
    ...versions.map(({ revision: item }) => {
      const label = `R${String(item.revisionNumber || 0).padStart(2, "0")}`;
      const on = item.id === revision?.id;
      return `<a href="${esc(projectRoute("preview", { structureId: lineageStructureId, structureName: structure.name, state: { revision: item.id, version: label } }))}" class="${on ? "on" : ""}" data-preview-version="${esc(item.id)}" aria-current="${on ? "page" : "false"}"><span>${label}</span><b>${esc(String(item.status || "candidate").replace(/[_-]+/g, " "))}</b><small>${esc(item.id.slice(0, 8))}</small></a>`;
    }),
  ].join("");
  const lineageTree = renderStructureRevisionTree(versions.map(({ revision: item }) => item), {
    structureId: lineageStructureId,
    selectedRevisionId: revision?.id || "",
    hrefFor: ({ source, revisionId }) => projectRoute("preview", {
      structureId: lineageStructureId,
      structureName: structure.name,
      state: source ? { version: "source" } : { revision: revisionId },
    }),
  });
  const status = selectedStatus;
  const ticketStatus = String(activity?.status || "").toLowerCase();
  const activeAgentWork = ["blocked", "ready", "leased", "in_progress"].includes(ticketStatus);
  const failedAttempt = !revision && ticketStatus === "failed";
  const currentStage = status === "approved" ? 4 : status === "review" ? 3 : revision ? 2 : (activeAgentWork || failedAttempt) ? 1 : 0;
  const stages = [
    ["Source specification", "Exact reusable module"],
    ["Agent work", ticketStatus === "blocked" ? "Blocked" : failedAttempt ? "Last attempt failed" : activeAgentWork ? "In progress" : revision ? "Recorded in provenance" : "Starts from the change chat"],
    ["Candidate version", revision ? versionLabel : "Not created"],
    ["Human review", status === "review" ? "Waiting for your decision" : "Required before approval"],
    ["Approved", status === "approved" ? "Project baseline" : "Not approved"],
  ];
  const ticketHref = activity ? projectRoute("ticket", { ticketId: activity.id, ticketName: activity.title || activity.type || "Structure change" }) : "";
  const decision = selectedDecision;
  const reviewHref = revision && ["review", "stale"].includes(status)
    ? projectRoute("revisionReview", { revisionId: revision.id, revisionName: `${structure.name} ${versionLabel}` })
    : "";
  const continueHref = revision
    ? projectRoute("structureChange", {
      structureId: structure.id,
      structureName: structure.name,
      state: {
        ...revisionState,
        ...(status === "rejected" && decision?.decision === "change_requested" ? { successor: revision.id } : {}),
      },
    })
    : "";
  const retryParentHref = revision
    ? projectRoute("structureChange", { structureId: structure.id, structureName: structure.name, state: { ...revisionState, retry: revision.id } })
    : "";
  const requestedChange = String(activity?.spec?.instruction || "").split("Requested change:").slice(1).join("Requested change:").trim();
  const activityBlock = activity ? `<article class="structure-preview-activity ${esc(ticketStatus)}"><span>${failedAttempt ? "ATTEMPT — NO VERSION CREATED" : "SOURCE TICKET"}</span><b>${failedAttempt ? "Last change attempt failed" : esc(activity.title || "Structure change")}</b>${requestedChange ? `<p><strong>Requested:</strong> ${esc(requestedChange)}</p>` : ""}<p>${failedAttempt ? "The run stopped before it produced an immutable candidate. SOURCE above is still the only viewable version." : `Agent work is ${esc(ticketStatus.replace(/[_-]+/g, " ") || "recorded")}.`}</p><div><a href="${esc(ticketHref)}">Open stages and run log →</a>${failedAttempt ? `<button type="button" class="primary" data-retry-structure-ticket="${esc(activity.id)}">Retry same request</button>` : ""}</div></article>` : `<p class="structure-preview-no-activity">No project version or agent work has been created from this source yet.</p>`;
  const decisionBlock = decision ? `<article class="structure-preview-decision"><span>HUMAN DECISION · ${esc(String(decision.decision || "recorded").replace(/[_-]+/g, " "))}</span><b>${esc(decision.feedback || "Decision recorded without written feedback.")}</b><small>${esc(decision.decidedBy || "Human reviewer")} · ${esc(when(decision.createdAt))}</small></article>` : "";

  $("#asset-list").innerHTML = `<div class="structure-preview-sidebar">
    <a href="${esc(dossierHref)}" class="structure-preview-back" data-back-to-structure>← ${structure.catalogSource ? "Back to Structures" : "Back to dossier"}</a>
    <header><small>SOURCE MODULE · ONE TWELFTH</small><h2>${esc(structure.name)}</h2><p>${esc(structure.summary)}</p></header>
    <section class="structure-preview-versions"><header><span>Version history</span><b>${versions.length} project version${versions.length === 1 ? "" : "s"}</b></header><nav aria-label="Versions of ${esc(structure.name)}">${versionLinks}</nav>
      <dl class="structure-preview-version-meta"><div><dt>Viewing</dt><dd>${esc(versionLabel)} · ${esc(versionStatus)}</dd></div>${revision ? `<div><dt>Created</dt><dd>${esc(when(revision.createdAt))}</dd></div><div><dt>Author</dt><dd>${esc(revision.createdBy || "Unknown")}</dd></div><div><dt>Immutable ID</dt><dd><code>${esc(revision.id.slice(0, 12))}</code></dd></div><div><dt>Content hash</dt><dd><code>${esc(revision.contentSha256 ? revision.contentSha256.slice(0, 12) : "Pending")}</code></dd></div>` : `<div><dt>Project state</dt><dd>No candidate or approved version</dd></div>`}</dl>
      ${lineageTree}
    </section>
    <div class="structure-preview-piece-count"><strong>1</strong><span><b>piece in this preview</b><small>The ring is not shown.</small></span></div>
    <dl>
      <div><dt>Source geometry</dt><dd>1 reusable segment</dd></div>
      <div><dt>Previewed pieces</dt><dd>1</dd></div>
      <div><dt>Centreline</dt><dd>${esc(String(segment.centrelineLengthM || "—"))} m</dd></div>
      <div><dt>Depth × height</dt><dd>${esc(`${segment.radialDepthM || "—"} × ${segment.totalHeightM || "—"}`)} m</dd></div>
      ${longitudinalOpening ? `<div><dt>Longitudinal opening</dt><dd>${esc(`${longitudinalOpening.clearRadialWidthM} m clear × ${longitudinalOpening.clearHeightM} m high`)}</dd></div>` : ""}
      ${roadLayout ? `<div><dt>Road layout</dt><dd>${esc(`${roadLayout.laneCount} parallel lanes · ${roadLayout.laneWidthM} m each`)}</dd></div>` : ""}
      <div><dt>Placement status</dt><dd>Not placed</dd></div>
    </dl>
    <details><summary>Exact connection-plane vertices</summary><ol>${vertices.map(([name, vertex]) => `<li><b>${esc(name)}</b><span>X ${esc(String(vertex.x))} m · Y ${esc(String(vertex.y))} m</span></li>`).join("")}</ol></details>
    <details><summary>Optional future ring · 12 placements</summary><p>This same source mesh may later be placed 12 times at ${esc(String(assembly.placementRadiusCm || "—"))} cm with ${esc(String(segment.angularSpacingDeg || "—"))}° spacing. That assembly is separate from this preview.</p></details>
    <div class="structure-preview-sidebar-actions"><a href="${esc(proposalHref)}" class="primary building-primary" data-propose-preview>${esc(proposalLabel)}</a><a href="${esc(specificationHref)}" class="building-route-link" data-open-preview-details>View exact specification</a></div>
  </div>`;

  $("#detail-body").innerHTML = `<div class="structure-preview-detail">
    <div class="structure-preview-notice"><b>${longitudinalOpening ? "One tunnel segment" : "One source segment"} · not placed</b><p>This live view contains exactly one browser-generated planning proxy at the origin.${longitudinalOpening ? " Its X-axis opening runs end to end through this one segment. Inspect both uncapped mouths below; the yellow floor continues between them." : ""} It is not a full ring, a DCC-authored mesh, an Unreal package, or a production approval.</p>${longitudinalOpening ? `<div class="structure-preview-opening-actions"><button type="button" data-focus-tunnel-opening="positive">View End A</button><button type="button" data-focus-tunnel-opening="negative">View End B</button><button type="button" data-frame-tunnel>Whole piece</button></div>` : ""}</div>
    <section class="structure-preview-progress" aria-label="Progress for ${esc(versionLabel)}"><header><span>Progress of this version</span><b>${esc(versionLabel)} · ${esc(versionStatus)}</b></header><ol>${stages.map(([label, detail], index) => `<li class="${index < currentStage ? "done" : index === currentStage ? (failedAttempt ? "failed" : "current") : ""}"><span>${String(index + 1).padStart(2, "0")}</span><div><b>${esc(label)}</b><small>${esc(detail)}</small></div></li>`).join("")}</ol>${activityBlock}${decisionBlock}${renderStructureReviewChoices({ revision, reviewDecision: decision, continueHref, retryParentHref, reviewHref })}<div class="structure-preview-progress-actions"><a href="${esc(projectRoute("board"))}">All agent work</a><a href="${esc(projectRoute("reviews"))}">Needs You</a>${reviewHref ? `<a class="primary" href="${esc(reviewHref)}">Review ${esc(versionLabel)}</a>` : ""}</div></section>
    <h3>Exact source geometry</h3>
    <dl>
      <div><dt>Centreline</dt><dd>${esc(String(segment.centrelineLengthM))} m</dd></div>
      <div><dt>Inner / outer faces</dt><dd>${esc(`${segment.innerFaceLengthM} / ${segment.outerFaceLengthM}`)} m</dd></div>
      <div><dt>Depth / height</dt><dd>${esc(`${segment.radialDepthM} / ${segment.totalHeightM}`)} m</dd></div>
      <div><dt>Vertical span</dt><dd>${esc(`${segment.baseZM} to ${segment.topZM}`)} m</dd></div>
      <div><dt>End mitre</dt><dd>${esc(String(segment.endMitreDeg))}° per end</dd></div>
      <div><dt>Midpoint radius</dt><dd>${esc(String(assembly.midpointRadiusM || "—"))} m <small>optional future 12-piece assembly</small></dd></div>
      <div><dt>Local transform</dt><dd>Origin · yaw 0° · scale 1</dd></div>
      ${longitudinalOpening ? `<div><dt>Tunnel axis</dt><dd>Local X · end to end</dd></div><div><dt>Clear opening</dt><dd>${esc(`${longitudinalOpening.clearRadialWidthM} × ${longitudinalOpening.clearHeightM} m`)}</dd></div><div><dt>Floor / roof shell</dt><dd>${esc(`${longitudinalOpening.floorThicknessM} / ${longitudinalOpening.roofThicknessM} m`)}</dd></div><div><dt>Left / right walls</dt><dd>${esc(`${longitudinalOpening.leftWallThicknessM} / ${longitudinalOpening.rightWallThicknessM} m`)}</dd></div>` : ""}
      ${roadLayout ? `<div><dt>Parallel roads</dt><dd>${esc(`${roadLayout.laneCount} × ${roadLayout.laneWidthM} m lanes`)}</dd></div><div><dt>Shoulders / median</dt><dd>${esc(`${roadLayout.shoulderWidthM} / ${roadLayout.medianWidthM} m`)}</dd></div><div><dt>Used / spare width</dt><dd>${esc(`${roadLayout.usedWidthM} / ${roadLayout.remainingClearanceM} m`)}</dd></div>` : ""}
    </dl>
    <h3>Export</h3>
    <div class="structure-preview-export locked"><b>Available after the asset is finished</b><p>This preview is only a source specification. Export unlocks after a versioned mesh passes validation and human approval; Unreal integration happens later as a separate job.</p><button type="button" disabled>Finish asset before export</button><details><summary>Future export settings</summary><dl><div><dt>Intended content path</dt><dd><code>${esc(unreal.staticMeshPath || "—")}</code></dd></div><div><dt>Materials</dt><dd>${esc(Object.keys(materials).join(" · ") || "—")}</dd></div><div><dt>Collision</dt><dd>${esc(unreal.collision || "—")}</dd></div></dl></details></div>
    <a href="${esc(proposalHref)}" class="primary building-primary" data-propose-preview>${esc(proposalLabel)}</a>
    <a href="${esc(dossierHref)}" class="building-route-link" data-back-to-structure>Return to ${structure.catalogSource ? "Structures" : "Structure dossier"}</a>
  </div>`;
  document.querySelectorAll("[data-back-to-structure]").forEach((link) => link.onclick = (event) => {
    if (!isUnmodifiedPrimaryLinkClick(event)) return;
    event.preventDefault();
    if (backRoute === "structures") navigateRoute("structures", { state: revisionState });
    else navigateRoute("structure", { structureId: structure.id, structureName: structure.name, state: revisionState });
  });
  $("[data-open-preview-details]")?.addEventListener("click", (event) => {
    if (!isUnmodifiedPrimaryLinkClick(event)) return;
    event.preventDefault();
    history.replaceState({}, "", projectRoute("preview", { structureId: lineageStructureId, structureName: structure.name, state: { ...selectedVersionState, pane: "spec" } }));
    activeRoute = parseRoute(location);
    if (mobileInspectorMedia.matches) openMobileInspector();
    else {
      $("#detail-body")?.scrollTo({ top: 0, behavior: "smooth" });
      $("#detail")?.classList.add("spec-highlight");
      setTimeout(() => $("#detail")?.classList.remove("spec-highlight"), 1200);
    }
  });
  document.querySelectorAll("[data-propose-preview]").forEach((link) => link.addEventListener("click", (event) => {
    if (!isUnmodifiedPrimaryLinkClick(event)) return;
    event.preventDefault();
    if (proposalNeedsReview) {
      navigateRoute("revisionReview", { revisionId: revision.id, revisionName: `${structure.name} ${versionLabel}` });
      return;
    }
    if (proposalStopped) {
      navigateRoute("structure", { structureId: structure.id, structureName: structure.name });
      return;
    }
    navigateRoute("structureChange", {
      structureId: structure.id,
      structureName: structure.name,
      state: {
        ...revisionState,
        ...(status === "rejected" && decision?.decision === "change_requested" ? { successor: revision.id } : {}),
      },
    });
  }));
  document.querySelectorAll("[data-preview-version]").forEach((link) => link.addEventListener("click", (event) => {
    if (!isUnmodifiedPrimaryLinkClick(event)) return;
    event.preventDefault();
    const value = link.dataset.previewVersion;
    navigateRoute("preview", { structureId: lineageStructureId, structureName: structure.name, state: value === "source" ? { version: "source" } : { revision: value } });
  }));
  document.querySelector("[data-retry-structure-ticket]")?.addEventListener("click", async (event) => {
    const button = event.currentTarget;
    button.disabled = true;
    button.textContent = "Retrying…";
    try {
      await api(`/tickets/${encodeURIComponent(button.dataset.retryStructureTicket)}/retry`, { method: "POST", body: "{}" });
      toast("Same request queued for another agent attempt");
      await applyRoute(parseRoute(location));
    } catch (error) {
      button.disabled = false;
      button.textContent = "Retry same request";
      toast(`Could not retry Structure request: ${String(error)}`, true);
    }
  });
  document.querySelectorAll("[data-focus-tunnel-opening]").forEach((button) => button.addEventListener("click", () => {
    viewer.focusRadialOpening(button.dataset.focusTunnelOpening);
  }));
  document.querySelector("[data-frame-tunnel]")?.addEventListener("click", () => viewer.frame());
  $("#drop-hint").style.display = "none";
  currentDetail = null;
  try {
    currentStats = viewer.loadRadialStructure(structure, { scope: "source_segment" });
    renderStats();
  } catch (error) {
    viewer.clear();
    currentStats = null;
    renderStats();
    $("#drop-hint").style.display = "flex";
    $("#drop-hint").textContent = "This structure revision does not contain complete preview geometry.";
    toast(`Three.js preview unavailable: ${String(error)}`, true);
  }
}

function switchView(view) {
  const previousView = currentView;
  if (view !== "structure-preview") {
    currentRadialPreview = null;
    currentStructurePreviewContext = null;
  }
  if (view !== "level" && terrainEditor?.active) terrainEditor.deactivate({ restore: false });
  if (view !== "visualizer" && megastructureStudio.active) megastructureStudio.deactivate();
  if (view !== "level") {
    terrainBtn.classList.remove("on");
    terrainBtn.textContent = "terrain lab";
  }
  currentView = view;
  document.body.dataset.view = view;
  structures.setActive(view === "buildings");
  closeMobileInspector();
  clearInterval(chainTimer);
  const navigationView = view === "structure-preview" ? "buildings" : view;
  document.querySelectorAll("[data-view]").forEach((b) => b.classList.toggle("on", b.dataset.view === navigationView));
  const threeCol = view === "assets" || view === "level" || view === "structure-preview" || view === "visualizer";
  const previousThreeCol = previousView === "assets" || previousView === "level" || previousView === "structure-preview" || previousView === "visualizer";
  if (previousThreeCol && previousView !== view) viewer.clear();
  if (!threeCol || previousView !== view) viewer.setFlyMode(false);
  panelEl.style.display = threeCol ? "none" : "";
  $("#sidebar").style.display = threeCol && view !== "visualizer" ? "" : "none";
  $("#stage").style.display = threeCol ? "" : "none";
  $("#detail").style.display = threeCol ? "" : "none";
  filterBoxEl.style.display = view === "assets" ? "" : "none";
  worldPicker.style.display = view === "level" ? "" : "none";
  $("#detail-tabs").style.display = view === "structure-preview" || view === "visualizer" ? "none" : "";
  feedbackBtn.hidden = view === "structure-preview" || view === "visualizer";
  if (view === "studio") return studio.render();
  else if (view === "buildings") return structures.render();
  else if (view === "structure-preview") return renderRadialStructurePreview();
  else if (view === "visualizer") return;
  else if (view === "assets") {
    if (previousView !== "assets") prepareAssetsView();
    return loadAssets();
  }
  else if (view === "level") return loadWorldList().then(loadWorld);
  else if (view === "tickets") return board.render();
  else if (view === "inbox") return reviews.render();
}
document.querySelectorAll("[data-view]").forEach((b) => (b.onclick = (event) => {
  if (!isUnmodifiedPrimaryLinkClick(event)) return;
  event.preventDefault();
  navigateView(b.dataset.view);
}));

async function loadWorldList() {
  const projectId = studio.getProjectId();
  if (!projectId) {
    worldPicker.innerHTML = `<option value="">Select a project first</option>`;
    currentWorldId = "";
    return;
  }
  const { worlds } = await api(`/worlds?projectId=${encodeURIComponent(projectId)}`);
  worldNames = new Map(worlds.map((world) => [world.worldId, world.name]));
  const nameCounts = worlds.reduce((counts, world) => {
    counts.set(world.name, (counts.get(world.name) ?? 0) + 1);
    return counts;
  }, new Map());
  const nameIndexes = new Map();
  worldPicker.innerHTML = worlds.length
    ? worlds.map((w) => {
        const index = (nameIndexes.get(w.name) ?? 0) + 1;
        nameIndexes.set(w.name, index);
        const duplicate = nameCounts.get(w.name) > 1
          ? ` · ${index}/${nameCounts.get(w.name)} · ${String(w.worldId).slice(0, 8)}`
          : "";
        return `<option value="${esc(w.worldId)}" title="${esc(w.brief ?? "")}">${esc(w.name)}${esc(duplicate)} (${w.cellCount} sections)</option>`;
      }).join("")
    : `<option value="">No worlds yet</option>`;
  if (!worlds.some((w) => w.worldId === currentWorldId)) {
    currentWorldId = worlds[0]?.worldId ?? "";
    localStorage.setItem("wl_world", currentWorldId);
  }
  worldPicker.value = currentWorldId;
  if (activeRoute.route === "worlds" && currentWorldId) {
    history.replaceState({}, "", projectRoute("world", { worldId: currentWorldId, worldName: worldNames.get(currentWorldId) }));
    activeRoute = parseRoute(location);
    pendingRoute = activeRoute;
    updateNavigation();
  } else if (activeRoute.route === "world" && currentWorldId) {
    history.replaceState({}, "", projectRoute("world", { worldId: currentWorldId, worldName: worldNames.get(currentWorldId), state: activeRoute.state }));
    activeRoute = parseRoute(location);
    pendingRoute = activeRoute;
    updateNavigation();
  }
}

async function loadWorld() {
  const requestedWorldId = currentWorldId;
  if (!currentWorldId) {
    $("#asset-list").innerHTML = `<p class="muted" style="padding:12px">No world map yet — begin with the World Plan stage in <b>Studio</b>.</p>`;
    viewer.clear();
    $("#statsbar").innerHTML = "";
    return;
  }
  worldData = await api(`/world/${currentWorldId}`);
  const list = $("#asset-list");
  if (!worldData.layout) {
    list.innerHTML = `<p class="muted" style="padding:12px">No approved layout yet. Check <b>Needs you</b> for a candidate revision, or return to Studio.</p>`;
    viewer.clear();
    $("#drop-hint").style.display = "flex";
    $("#drop-hint").textContent = "No world layout yet.";
    $("#statsbar").innerHTML = "";
    return;
  }
  $("#drop-hint").style.display = "none";
  const manifestsByCell = new Map(worldData.manifests.map((m) => [m.cellId, m]));
  list.innerHTML = "";
  const terrainLaunch = document.createElement("button");
  terrainLaunch.type = "button";
  terrainLaunch.className = "terrain-launch-card";
  terrainLaunch.innerHTML = `<span>PROCEDURAL TERRAIN</span><b>Open Terrain Lab</b><small>Rule-based landforms · spatial voice · movable POIs</small>`;
  terrainLaunch.onclick = () => terrainBtn.click();
  list.appendChild(terrainLaunch);
  const mobileHint = document.createElement("p");
  mobileHint.className = "horizontal-scroll-hint world-scroll-hint";
  mobileHint.textContent = "Swipe sideways to inspect every world section →";
  list.appendChild(mobileHint);
  for (const cell of worldData.layout.doc.cells) {
    const m = manifestsByCell.get(cell.cellId);
    const item = document.createElement("a");
    item.className = "asset-item";
    item.href = projectRoute("section", { worldId: currentWorldId, worldName: worldNames.get(currentWorldId), sectionId: cell.cellId, sectionName: cell.name });
    item.dataset.sectionId = cell.cellId;
    item.innerHTML = `<div class="noimg">▦</div>
      <div class="meta"><div class="name">${esc(cell.name)}</div>
      <div class="sub">${m ? badge(m.status) : badge("neutral")} <span>${m ? `${m.doc.placements.length} placements` : "not populated"}</span></div></div>`;
    item.onclick = (event) => {
      if (!isUnmodifiedPrimaryLinkClick(event)) return;
      event.preventDefault();
      navigateRoute("section", { worldId: currentWorldId, worldName: worldNames.get(currentWorldId), sectionId: cell.cellId, sectionName: cell.name });
    };
    list.appendChild(item);
  }
  $("#detail-body").innerHTML = `<h3>World</h3><div class="card">${esc(worldData.layout.doc.brief)}</div>
    <p class="muted">Choose a section in the sidebar to inspect its contents. Placeholder boxes mark assets that are still in production.</p>`;
  if (pendingRoute.route === "section" && pendingRoute.worldId === currentWorldId) showWorldSection(pendingRoute.sectionId);
  const worldLoad = viewer.loadWorld(worldData);
  await loadFeedbackPins(list);
  if (pendingRoute.route === "feedback" && pendingRoute.worldId === currentWorldId) await showFeedbackFlow(pendingRoute.ticketId);
  const stats = await worldLoad;
  if (stats.cancelled || currentView !== "level" || currentWorldId !== requestedWorldId) return;
  $("#statsbar").innerHTML = `
    <span>layout <b>v${worldData.layout.version}</b></span>
    ${worldData.blockout ? `<span>blockout <b>v${worldData.blockout.version}</b> (${stats.solids} solids)</span>` : ""}
    <span>sections <b>${stats.cells}</b></span>
    <span>placements <b>${stats.placements}</b></span>
    <span>meshes <b>${stats.loaded}</b></span>
    <span class="${stats.placeholders ? "dim-warn" : ""}">placeholders <b>${stats.placeholders}</b></span>`;
  // The stage becomes measurable only after the World view is revealed. A
  // second-frame fit prevents the camera from retaining the hidden Asset
  // viewport's aspect and opening inside the greybox.
  const framedWorldId = currentWorldId;
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (currentView === "level" && currentWorldId === framedWorldId) viewer.frame();
  }));
}

function showWorldSection(sectionId) {
  const cell = worldData?.layout?.doc?.cells?.find((candidate) => candidate.cellId === sectionId);
  if (!cell) return;
  const manifest = worldData.manifests?.find((candidate) => candidate.cellId === sectionId);
  document.querySelectorAll("[data-section-id]").forEach((item) => item.classList.toggle("on", item.dataset.sectionId === sectionId));
  $("#detail-body").innerHTML = `<h3>${esc(cell.name)} (${esc(cell.cellId)})</h3><div class="card">
    <div class="row"><span class="k">purpose</span><span>${esc(cell.purpose)}</span></div>
    <div class="row"><span class="k">bounds cm</span><span>${cell.bounds.x},${cell.bounds.y} · ${cell.bounds.w}×${cell.bounds.h}</span></div>
    <div class="row"><span class="k">neighbors</span><span>${cell.neighbors.map(esc).join(", ") || "—"}</span></div>
    </div>${manifest ? `<h3>Manifest v${manifest.version}</h3><div class="card">${esc(manifest.doc.rationale ?? "")}
    <details><summary>placements</summary>${json(manifest.doc.placements)}</details></div>` : `<p class="muted">Not populated yet.</p>`}`;
  openMobileInspector();
  if (activeRoute.route === "section") {
    history.replaceState({}, "", projectRoute("section", {
      worldId: currentWorldId,
      worldName: worldNames.get(currentWorldId),
      sectionId: cell.cellId,
      sectionName: cell.name,
      state: activeRoute.state,
    }));
    activeRoute = parseRoute(location);
    pendingRoute = activeRoute;
  }
}

/* ---------- feedback flow: pins in 3D + timeline in the detail panel ---------- */

const PIN_COLORS = {
  ready: 0x8b8b9a, blocked: 0x8b8b9a, leased: 0x5c9de0, in_progress: 0x5c9de0,
  submitted: 0xe8b34b, review: 0xe8b34b, approved: 0x4bc47f,
  rejected: 0xe05c5c, failed: 0xe05c5c, escalated: 0xe05c5c, cancelled: 0x55555f, stale: 0x55555f,
};
let feedbackItems = [];

async function loadFeedbackPins(listEl) {
  const requestedWorldId = currentWorldId;
  const requestedWorldData = worldData;
  feedbackItems = [];
  if (!currentWorldId || !worldData?.layout) return;
  let tickets;
  try {
    const projectId = studio.getProjectId();
    if (!projectId) return;
    ({ tickets } = await api(`/tickets?projectId=${encodeURIComponent(projectId)}&type=revise&worldId=${encodeURIComponent(currentWorldId)}`));
  } catch { return; }
  if (currentView !== "level" || currentWorldId !== requestedWorldId || worldData !== requestedWorldData) return;
  if (!tickets.length) { viewer.setFeedbackPins([]); return; }

  const cells = worldData.layout.doc.cells;
  const solids = worldData.blockout?.doc?.solids ?? [];
  const floorTop = (cellId) => {
    const walkable = solids.filter((s) => s.cellId === cellId && ["floor", "platform", "ramp"].includes(s.role));
    return walkable.length ? Math.max(...walkable.map((s) => s.pos.z + s.size.z)) : 0;
  };

  const pins = [];
  tickets.forEach((t, i) => {
    const cellId = t.scope?.cellId;
    const cell = cells.find((c) => c.cellId === cellId);
    if (!cell) return;
    const n = pins.length + 1;
    pins.push({
      id: t.id, number: n,
      xCm: cell.bounds.x + cell.bounds.w / 2,
      yCm: cell.bounds.y + cell.bounds.h / 2,
      zCm: floorTop(cellId),
      color: PIN_COLORS[t.status] ?? 0x8b8b9a,
    });
    feedbackItems.push({ ...t, number: n });
  });
  viewer.setFeedbackPins(pins);
  viewer.onPinClick = (ticketId) => {
    const feedback = feedbackItems.find((item) => item.id === ticketId);
    navigateRoute("feedback", { worldId: currentWorldId, worldName: worldNames.get(currentWorldId), ticketId, ticketName: feedback?.title || feedback?.spec?.critique });
  };

  if (feedbackItems.length && listEl) {
    const sec = document.createElement("div");
    sec.innerHTML = `<div class="fb-header">FEEDBACK (${feedbackItems.length})</div>`;
    for (const f of feedbackItems) {
      const item = document.createElement("a");
      item.className = "asset-item";
      item.href = projectRoute("feedback", { worldId: currentWorldId, worldName: worldNames.get(currentWorldId), ticketId: f.id, ticketName: f.title || f.spec?.critique });
      item.innerHTML = `<div class="noimg fb-num">${f.number}</div>
        <div class="meta"><div class="name">${esc((f.spec?.critique ?? f.title).slice(0, 60))}</div>
        <div class="sub">${badge(f.status)} <span>${esc(f.scope?.cellId ?? "")}</span></div></div>`;
      item.onclick = (event) => {
        if (!isUnmodifiedPrimaryLinkClick(event)) return;
        event.preventDefault();
        navigateRoute("feedback", { worldId: currentWorldId, worldName: worldNames.get(currentWorldId), ticketId: f.id, ticketName: f.title || f.spec?.critique });
      };
      sec.appendChild(item);
    }
    listEl.appendChild(sec);
  }
}

/** The flow view: where a piece of feedback is in the pipeline, as a
 * step timeline — filed → queued → agent working → submitted → decision. */
async function showFeedbackFlow(ticketId) {
  const f = feedbackItems.find((x) => x.id === ticketId);
  let events = [];
  try { ({ events } = await api(`/tickets/${ticketId}`)); } catch {}
  const attachments = f?.spec?.attachments ?? [];
  const STAGES = [
    ["filed", ["ready", "blocked"]],
    ["agent working", ["leased", "in_progress"]],
    ["submitted", ["submitted"]],
    ["your review", ["review"]],
    ["applied", ["approved"]],
  ];
  const reached = new Set(events.map((e) => e.toStatus));
  const stageHtml = STAGES.map(([label, sts], i) => {
    const hit = sts.some((s) => reached.has(s)) || (i === 0 && events.length);
    const current = f && sts.includes(f.status);
    return `<span class="flow-step${hit ? " hit" : ""}${current ? " now" : ""}">${label}</span>`;
  }).join(`<span class="flow-arrow">→</span>`);

  $("#detail-body").innerHTML = `
    <h3>Feedback #${f?.number ?? ""} — ${badge(f?.status ?? "?")}</h3>
    <div class="card">
      <div class="flow">${stageHtml}</div>
      ${f?.status === "ready" ? `<p class="muted">Queued — no revise agent picks these up yet; it's the next pipeline piece.</p>` : ""}
      <div class="k">your note</div><div>“${esc(f?.spec?.critique ?? "")}”</div>
      ${attachments.map((k) => imagePreview({ uri: k, label: "Feedback attachment", alt: "Feedback attachment", esc, fileUrl, className: "refimg" })).join("")}
      <div class="row"><span class="k">scope</span><span>${esc(f?.scope?.cellId ?? f?.scope?.assetUid ?? "")}</span></div>
    </div>
    <h3>Timeline</h3><ul class="timeline">
      ${events.map((e) => `<li><span class="t">${when(e.at)}</span> ${e.fromStatus ? `${badge(e.fromStatus)}→` : ""}${badge(e.toStatus)}
        <span class="muted">${esc(e.actor)}${e.note ? ` · ${esc(e.note)}` : ""}</span></li>`).join("")}
    </ul>`;
  bindArtifactLightboxes($("#detail-body"));
  openMobileInspector();
  if (activeRoute.route === "feedback") {
    history.replaceState({}, "", projectRoute("feedback", {
      worldId: currentWorldId,
      worldName: worldNames.get(currentWorldId),
      ticketId,
      ticketName: f?.title || f?.spec?.critique || "feedback",
      state: activeRoute.state,
    }));
    activeRoute = parseRoute(location);
    pendingRoute = activeRoute;
  }
}

/* ---------- asset list ---------- */

let statusFilter = "";
let assetQuery = "";
const FILTERS = [["", "all"], ["draft", "draft"], ["approved", "approved"], ["placeholder", "placeholder"], ["deprecated", "deprecated"]];
const filterBox = filterBoxEl;
for (const [val, label] of FILTERS) {
  const b = document.createElement("button");
  b.textContent = label;
  if (val === "") b.classList.add("on");
  b.onclick = () => {
    statusFilter = val;
    filterBox.querySelectorAll("button").forEach((x) => x.classList.toggle("on", x === b));
    navigateRoute("assets", { state: { status: statusFilter, query: assetQuery } }, { replace: true, focus: false });
  };
  filterBox.appendChild(b);
}
const assetSearch = document.createElement("input");
assetSearch.id = "asset-search";
assetSearch.type = "search";
assetSearch.placeholder = "Search assets";
assetSearch.setAttribute("aria-label", "Search project assets");
assetSearch.addEventListener("input", () => {
  assetQuery = assetSearch.value.trim();
  clearTimeout(assetSearch._routeTimer);
  assetSearch._routeTimer = setTimeout(() => navigateRoute("assets", {
    state: { status: statusFilter, query: assetQuery },
  }, { replace: true, focus: false }), 180);
});
filterBox.appendChild(assetSearch);
$("#refresh-btn").onclick = () => {
  if (currentView === "level") loadWorld();
  else if (currentView === "assets") loadAssets();
  else if (currentView === "buildings") structures.refresh();
  else if (currentView === "inbox") reviews.render();
  else if (currentView === "tickets") board.render();
  else studio.refresh();
};

async function loadAssets() {
  const projectId = studio.getProjectId();
  const list = $("#asset-list");
  if (!projectId) {
    list.innerHTML = `<p class="muted" style="padding:12px">Select a project to view its assets.</p>`;
    viewer.clear();
    return;
  }
  const params = new URLSearchParams({ projectId });
  if (statusFilter) params.set("status", statusFilter);
  const response = await api(`/catalog?${params}`);
  const query = assetQuery.toLocaleLowerCase();
  const assets = (response.assets || []).filter((asset) => !query || [asset.displayName, asset.assetKind, asset.source, ...(asset.tags || [])].join(" ").toLocaleLowerCase().includes(query));
  list.innerHTML = assets.length ? "" : `<p class="muted" style="padding:12px">No assets${statusFilter ? ` with status ${statusFilter}` : ""}${assetQuery ? ` matching “${esc(assetQuery)}”` : ""}.</p>`;
  for (const a of assets) {
    const item = document.createElement("a");
    item.className = "asset-item";
    item.dataset.uid = a.assetUid;
    item.href = projectRoute("asset", { assetUid: a.assetUid, assetName: a.displayName, state: { tab: "pipeline", status: statusFilter, query: assetQuery } });
    item.innerHTML = `
      ${a.thumbnailUri ? `<img src="${esc(fileUrl(a.thumbnailUri))}" alt="" loading="lazy" /><div class="noimg image-fallback" hidden>◇</div>` : `<div class="noimg">◇</div>`}
      <div class="meta">
        <div class="name">${esc(a.displayName)}</div>
        <div class="sub">${badge(a.status)} <span>${esc(a.assetKind)}</span> <span>${esc(a.source)}</span></div>
      </div>`;
    item.querySelector("img")?.addEventListener("error", (event) => {
      event.currentTarget.hidden = true;
      event.currentTarget.nextElementSibling.hidden = false;
    });
    item.onclick = (event) => {
      if (!isUnmodifiedPrimaryLinkClick(event)) return;
      event.preventDefault();
      navigateRoute("asset", { assetUid: a.assetUid, assetName: a.displayName, state: { tab: "pipeline", status: statusFilter, query: assetQuery } });
    };
    list.appendChild(item);
  }
  const target = activeRoute.route === "asset" ? activeRoute.assetUid : "";
  if (target) {
    await selectAsset(target, { preserveReview: Boolean(activeAssetReview?.assetUid === target || activeRoute.state?.tab === "review") });
  } else {
    $("#drop-hint").style.display = "flex";
    $("#drop-hint").textContent = assets.length
      ? "Select an asset to inspect it in 3D"
      : "No project assets match this filter.";
  }
}

function prepareAssetsView() {
  currentDetail = null;
  currentStats = null;
  if (activeRoute.state?.tab !== "review") activeAssetReview = null;
  viewer.clear();
  renderStats();
  $("#asset-list").innerHTML = `<p class="muted workspace-loading">Loading project assets…</p>`;
  $("#drop-hint").style.display = "flex";
  $("#drop-hint").textContent = "Loading project assets…";
  $("#detail-body").innerHTML = `<p class="muted">Choose an asset to inspect its project-scoped revisions and provenance.</p>`;
}

async function selectAsset(uid, { preserveReview = false } = {}) {
  if (!preserveReview) activeAssetReview = null;
  endCompare();
  document.querySelectorAll(".asset-item").forEach((x) => x.classList.toggle("on", x.dataset.uid === uid));
  try {
    currentDetail = await api(`/catalog/${uid}/detail`);
  } catch (err) {
    return toast(String(err), true);
  }
  if (activeRoute.route === "asset") {
    history.replaceState({}, "", projectRoute("asset", {
      assetUid: uid,
      assetName: currentDetail.asset.displayName,
      state: activeRoute.state,
    }));
    activeRoute = parseRoute(location);
    pendingRoute = activeRoute;
    updateNavigation();
  }
  // the CURRENT version's snapshot is authoritative (decal revisions aren't
  // in the provenance-artifact chain); artifacts are the fallback
  const glbUri =
    currentDetail.versions?.find((v) => v.version === currentDetail.asset.version && v.glbKey)?.glbKey ??
    currentDetail.artifacts.find((a) => a.kind === "gltf" || a.uri.endsWith(".glb"))?.uri;
  $("#drop-hint").style.display = glbUri ? "none" : "flex";
  $("#drop-hint").textContent = glbUri ? "" : "No mesh artifact for this asset yet (placeholder or pre-generation).";
  currentStats = null;
  assetEvidenceLoaded = false;
  assetEvidenceError = "";
  if (glbUri) {
    try {
      currentStats = await viewer.load(fileUrl(glbUri));
      assetEvidenceLoaded = true;
    } catch (err) {
      if (err?.name === "AbortError") return;
      assetEvidenceError = String(err);
      toast(`GLB load failed: ${err}`, true);
    }
  } else {
    viewer.clear();
    if (activeAssetReview) assetEvidenceError = "No reviewable mesh was submitted for this asset.";
  }
  renderStats();
  updateDetailTabs();
  renderDetail();
  openMobileInspector();
}

/* ---------- detail panel ---------- */

document.querySelectorAll("#detail-tabs button").forEach((b) => {
  b.onclick = () => {
    currentTab = b.dataset.tab;
    document.querySelectorAll("#detail-tabs button").forEach((x) => x.classList.toggle("on", x === b));
    renderDetail();
    if (currentDetail?.asset?.assetUid && activeRoute.route === "asset") {
      navigateRoute("asset", { assetUid: currentDetail.asset.assetUid, assetName: currentDetail.asset.displayName, state: { ...activeRoute.state, tab: currentTab } }, { replace: true, focus: false });
    }
  };
});

function updateDetailTabs() {
  const reviewTab = document.querySelector('#detail-tabs [data-tab="review"]');
  if (!reviewTab) return;
  reviewTab.hidden = !activeAssetReview;
  if (!activeAssetReview && currentTab === "review") currentTab = "pipeline";
  document.querySelectorAll("#detail-tabs button").forEach((button) => {
    button.classList.toggle("on", button.dataset.tab === currentTab);
  });
}

function renderDetail() {
  const body = $("#detail-body");
  $("#review-return").hidden = !(activeAssetReview && currentTab === "review");
  if (!currentDetail) {
    body.innerHTML = `<p class="muted">${currentStats ? "Local file preview — no pipeline data." : "Select an asset."}</p>`;
    return;
  }
  if (currentTab === "review" && activeAssetReview) body.innerHTML = assetReviewHtml(activeAssetReview);
  else if (currentTab === "pipeline") body.innerHTML = pipelineHtml(currentDetail);
  else if (currentTab === "textures") renderTextures(body);
  else body.innerHTML = json(currentDetail.asset.doc);
  bindActions(body);
  bindArtifactLightboxes(body);
  if (currentTab === "pipeline") trackFeedbackChains(currentDetail);
}

function assetReviewHtml(review) {
  const { ticket, events = [], artifacts = [] } = review;
  const attachments = [
    ...(Array.isArray(ticket.spec?.attachments) ? ticket.spec.attachments : []),
    ticket.spec?.referenceImageKey,
    ticket.spec?.referenceImageUrl,
  ].filter(Boolean);
  const images = [
    ...attachments.map((uri) => ({ uri, label: "Submitted reference" })),
    ...artifacts.filter((artifact) => /\.(png|jpe?g|webp|gif|avif)(\?.*)?$/i.test(artifact.uri)).map((artifact) => ({ uri: artifact.uri, label: artifact.kind || "Output image" })),
  ];
  const seen = new Set();
  const uniqueImages = images.filter((image) => image.uri && !seen.has(image.uri) && seen.add(image.uri));
  const requested = ticket.spec?.critique || ticket.spec?.brief || ticket.spec?.prompt || "Review the submitted asset against the brief and references.";
  const evidenceReady = assetEvidenceLoaded && !assetEvidenceError;
  return `<section class="asset-review">
    <div class="asset-review-head">
      <span class="badge review">approval</span>
      <h3>Approve ${esc(currentDetail?.asset?.displayName || ticket.title || "asset")}</h3>
      <p>The model is open in the normal asset workspace. Inspect it here, compare versions, or draw feedback directly on it before deciding.</p>
    </div>
    <div class="asset-review-card">
      <span class="k">What this work is improving</span>
      <p>${esc(String(requested))}</p>
      <div class="asset-review-meta"><span>${esc(ticket.type)}</span><span>${when(ticket.updatedAt || ticket.createdAt)}</span><span>attempt ${ticket.attempt}/${ticket.maxAttempts}</span></div>
    </div>
    <section class="review-evidence ${evidenceReady ? "ready" : "blocked"}" role="status">
      <strong>${evidenceReady ? "3D evidence loaded" : "Approval blocked — evidence unavailable"}</strong>
      <p>${evidenceReady ? "The pinned mesh is loaded in this workspace and ready for inspection." : esc(assetEvidenceError || "The submitted mesh has not loaded yet. Retry before approving this work.")}</p>
      ${evidenceReady ? "" : `<button type="button" data-review-retry>Retry evidence load</button>`}
      <button type="button" data-review-inspect-model>Inspect model</button>
    </section>
    ${uniqueImages.length ? `<section class="asset-review-images"><h3>Visual context</h3><div class="asset-review-image-grid">
      ${uniqueImages.map((image, index) => `<button type="button" class="asset-review-image" data-review-image="${esc(image.uri)}" data-review-image-label="${esc(image.label)}" aria-label="Open ${esc(image.label)}"><img src="${esc(fileUrl(image.uri))}" alt="${esc(image.label)}" loading="lazy" /><span>${esc(image.label)} ${uniqueImages.length > 1 ? index + 1 : ""}</span></button>`).join("")}
    </div></section>` : ""}
    ${artifacts.filter((artifact) => !/\.(png|jpe?g|webp|gif|avif)(\?.*)?$/i.test(artifact.uri)).length ? `<details class="asset-review-files"><summary>Other submitted files</summary>${artifacts.filter((artifact) => !/\.(png|jpe?g|webp|gif|avif)(\?.*)?$/i.test(artifact.uri)).map((artifact) => `<div>${esc(artifact.kind || "artifact")} · ${artifactLink({ uri: artifact.uri, label: artifact.uri.split("/").pop(), esc, fileUrl })}</div>`).join("")}</details>` : ""}
    <details class="asset-review-files"><summary>Decision history</summary><ul class="timeline">${events.map((event) => `<li><span class="t">${when(event.at)}</span>${badge(event.toStatus)}<span class="muted">${esc(event.note || event.actor || "")}</span></li>`).join("") || "<li class=\"muted\">No recorded history.</li>"}</ul></details>
    <section class="asset-review-decision">
      <label for="asset-review-note">Decision note <span>— required when requesting changes</span></label>
      <textarea id="asset-review-note" rows="4" placeholder="What works, what should change, and why?"></textarea>
      <div class="review-decision-buttons"><button type="button" class="approve" data-asset-review-action="approved" ${evidenceReady ? "" : "disabled aria-disabled=\"true\" title=\"Load the submitted mesh before approving\""}>Approve asset</button><button type="button" class="reject" data-asset-review-action="rejected">Request changes</button></div>
      <button type="button" class="asset-review-back" data-asset-review-back>Back to Needs you</button>
    </section>
  </section>`;
}

function showReviewImage(uri, label) {
  openArtifactLightbox({ src: fileUrl(uri), label });
}

/* ---------- live feedback-chain status: queue position, stage, ETA ---------- */

let chainTimer = null;

async function trackFeedbackChains(d) {
  clearInterval(chainTimer);
  const critiques = d.critiques ?? [];
  if (!critiques.length) return;

  const tick = async () => {
    let anyActive = false;
    for (const cr of critiques) {
      const el = document.querySelector(`[data-chain="${cr.id}"]`);
      if (!el) { clearInterval(chainTimer); return; }
      try {
        // refresh the revise ticket + its regeneration ticket (the real work)
        const { ticket: revise, events: reviseEvents } = await api(`/tickets/${cr.id}`);
        const regenId = revise.result?.regenerationTicketId;
        const regenRes = regenId ? await api(`/tickets/${regenId}`) : null;
        const { html, active } = chainHtml(revise, regenRes?.ticket ?? null, [
          ...(reviseEvents ?? []).map((e) => ({ ...e, src: "triage" })),
          ...((regenRes?.events ?? []).map((e) => ({ ...e, src: "fix" }))),
        ]);
        el.innerHTML = html;
        anyActive = anyActive || active;
      } catch { el.innerHTML = `<span class="muted">status unavailable</span>`; }
    }
    if (!anyActive) clearInterval(chainTimer);
  };
  await tick();
  chainTimer = setInterval(tick, 10000);
}

/** One feedback's journey with stage + ETA. Returns active=true while
 * anything is still moving (keeps the 10s poll alive). */
function chainHtml(revise, regen, events = []) {
  const mins = (t) => Math.max(0, Math.round((Date.now() - new Date(t)) / 60000));
  const ago = (t) => {
    const m = mins(t);
    return m < 1 ? "just now" : m < 60 ? `${m}m ago` : `${Math.floor(m / 60)}h ${m % 60}m ago`;
  };
  const steps = [];
  let now = "", active = true;

  steps.push({ label: "filed", done: true });

  const triageDone = Boolean(revise.result);
  if (["ready", "blocked"].includes(revise.status)) {
    steps.push({ label: "triage", current: true });
    now = `queued for triage — agents pick up new work every minute (waiting ${mins(revise.createdAt)}m)`;
  } else if (["leased", "in_progress"].includes(revise.status)) {
    steps.push({ label: "triage", current: true });
    now = "AI is reading your note + drawing (vision) — usually under a minute";
  } else if (revise.status === "failed" || revise.status === "escalated") {
    steps.push({ label: "triage", error: true });
    now = "triage failed — needs a re-queue (see Inbox)";
    active = false;
  } else steps.push({ label: `triage: ${revise.result?.changeType ?? "done"}`, done: true });

  if (triageDone && revise.result?.decalApplied) {
    steps.push({ label: "graphic generated", done: true });
    steps.push({ label: "placed at your mark", done: true });
    now = `DECAL APPLIED exactly where you drew → v${revise.result.newVersion} — re-select the asset to see it and compare versions`;
    active = false;
  } else if (triageDone && revise.result?.scaleApplied) {
    const f = revise.result.factors ?? {};
    steps.push({ label: `scaled ×${f.x ?? 1}/${f.y ?? 1}/${f.z ?? 1}`, done: true });
    now = `SCALE APPLIED → v${revise.result.newVersion} — re-select the asset to see it and compare versions`;
    active = false;
  } else if (triageDone && !regen) {
    steps.push({ label: "plan recorded", done: true, current: true });
    const recordedAt = revise.updatedAt ?? revise.createdAt;
    now =
      `${revise.result.changeType} change — plan recorded ${ago(recordedAt)}. ` +
      `Applying ${revise.result.changeType} changes is NOT automated yet: no ETA — it waits for a human (or a future executor) to act on the plan.`;
    active = false;
  }

  if (regen) {
    const isRetexture = Boolean(regen.spec?.retextureFromGlbKey);
    const fixLabel = isRetexture ? "repaint texture" : "regenerate mesh";
    const eta = isRetexture ? "~2–4 min" : "~5–8 min";
    if (["ready", "blocked"].includes(regen.status)) {
      steps.push({ label: fixLabel, current: true });
      now = `fix queued (${eta} once started; waiting ${mins(regen.createdAt)}m)`;
    } else if (["leased", "in_progress"].includes(regen.status)) {
      steps.push({ label: fixLabel, current: true });
      now = `${isRetexture ? "repainting your existing mesh (geometry preserved)" : "generating new geometry"} — ${eta}, running ${mins(regen.updatedAt)}m`;
    } else if (regen.status === "submitted") {
      steps.push({ label: fixLabel, done: true });
      steps.push({ label: "mechanical QA", current: true });
      now = "delivered — automatic QA checking tris/dimensions/textures (seconds)";
    } else if (regen.status === "review") {
      steps.push({ label: fixLabel, done: true });
      steps.push({ label: "QA passed", done: true });
      steps.push({ label: "your review", current: true });
      now = "NEW VERSION READY — re-select the asset to compare old/new, then approve/reject in the Inbox";
      active = false;
    } else if (regen.status === "rejected") {
      steps.push({ label: fixLabel, done: true });
      steps.push({ label: "QA rejected", error: true });
      now = "the fix failed mechanical QA — new version exists in history but is off-spec (see Inbox for the reason)";
      active = false;
    } else if (regen.status === "failed" || regen.status === "escalated") {
      steps.push({ label: fixLabel, error: true });
      now = "generation failed — needs a re-queue (see Inbox)";
      active = false;
    } else {
      steps.push({ label: `${fixLabel}: ${regen.status}`, done: true });
      active = false;
    }
  }

  const chips = steps
    .map((s) => `<span class="flow-step${s.done ? " hit" : ""}${s.current ? " now" : ""}${s.error ? " err" : ""}">${s.label}</span>`)
    .join(`<span class="flow-arrow">→</span>`);

  // full state-change log: every transition, timestamped, with the delta
  // since the previous one — how long each stage actually took
  const sorted = [...events].sort((a, b) => new Date(a.at) - new Date(b.at));
  const logRows = sorted
    .map((e, i) => {
      const delta = i === 0 ? "" : ` <span class="delta">+${Math.max(0, Math.round((new Date(e.at) - new Date(sorted[i - 1].at)) / 1000))}s</span>`;
      return `<li><span class="t">${new Date(e.at).toLocaleTimeString()}</span>
        <span class="src">${esc(e.src)}</span>
        ${e.fromStatus ? `${badge(e.fromStatus)}→` : ""}${badge(e.toStatus)}${delta}
        <span class="muted">${esc(e.actor)}${e.note ? ` · ${esc(e.note).slice(0, 90)}` : ""}</span></li>`;
    })
    .join("");
  const last = sorted[sorted.length - 1];
  const log = sorted.length
    ? `<details class="state-log"><summary>state log — ${sorted.length} changes, last ${ago(last.at)}</summary><ul class="timeline">${logRows}</ul></details>`
    : "";

  return { html: `<div class="flow">${chips}</div><div class="chain-now">${esc(now)}</div>${log}`, active };
}

function pipelineHtml(d) {
  let html = "";
  const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(4)}`);

  html += `<h3>Asset</h3><div class="card">
    <div class="row"><span class="k">name</span><b>${esc(d.asset.displayName)}</b></div>
    <div class="row"><span class="k">status / version</span><span>${badge(d.asset.status)} <b>v${d.asset.version}</b></span></div>
    <div class="row"><span class="k">uid</span><span>${esc(d.asset.assetUid)}</span></div>
    <div class="row"><span class="k">tags</span><span>${d.asset.tags.map(esc).join(", ")}</span></div>
    <div class="row"><span class="k">created</span><span>${when(d.asset.createdAt)}</span></div>
  </div>`;

  const safeName = d.asset.displayName.replace(/[^a-zA-Z0-9_-]+/g, "_");
  const currentGlb =
    (d.versions ?? []).find((v) => v.version === d.asset.version && v.glbKey)?.glbKey ??
    d.artifacts.find((a) => a.kind === "gltf" || a.uri.endsWith(".glb"))?.uri;
  const exportReady = d.asset.status === "approved" && Boolean(currentGlb);
  html += `<h3>Export</h3><section class="asset-export-step ${exportReady ? "ready" : "locked"}">
    <header><div><small>AFTER THE ASSET IS FINISHED</small><b>${exportReady ? "Approved source ready" : "Export locked"}</b></div><span>${exportReady ? "Ready" : "Waiting"}</span></header>
    <p>${exportReady ? "Download the approved, versioned source package. Importing it into Unreal is a later integration job with its own workspace, validation, and approval." : "Finish validation and human approval before this asset can leave authoring."}</p>
    ${exportReady ? `<a class="btn-like primary" href="${esc(fileUrl(currentGlb))}" download="${esc(safeName)}_v${d.asset.version}.glb">Export approved source (.glb)</a>` : `<button type="button" disabled>Finish asset before export</button>`}
    <small>This step exports source only; it does not change an Unreal project.</small>
  </section>`;

  html += `<h3>Versions</h3><div class="card">`;
  const versions = d.versions ?? [];
  if (!versions.length) html += `<p class="muted">No version history yet.</p>`;
  for (const v of versions) {
    const current = v.version === d.asset.version;
    html += `<div class="row"><span class="k">v${v.version}${current ? " (current)" : ""}</span>
      <span>${when(v.createdAt)}
      ${v.glbKey
        ? `<button data-load-glb="${esc(v.glbKey)}" data-vnum="${v.version}">view</button>
           <a class="btn-like" href="${esc(fileUrl(v.glbKey))}" download="${esc(safeName)}_v${v.version}.glb" title="Download v${v.version}">Download</a>`
        : `<span class="muted">no mesh snapshot</span>`}</span></div>`;
  }
  const comparable = versions.filter((v) => v.glbKey);
  if (comparable.length >= 2) {
    html += `<button class="primary" data-compare='${esc(JSON.stringify(comparable.map((v) => ({ v: v.version, key: v.glbKey }))))}'>⇄ compare old/new (same camera)</button>`;
  } else if (versions.length < 2) {
    html += `<p class="muted">Regenerations (e.g. from feedback) create new versions here, with old/new compare.</p>`;
  }
  html += `</div>`;

  if ((d.critiques ?? []).length) {
    html += `<h3>Feedback on this asset</h3>`;
    for (const cr of d.critiques) {
      html += `<div class="card">
        <div class="row"><b>${when(cr.createdAt)}</b><span class="muted">${esc(cr.id.slice(0, 8))}</span></div>
        <div>“${esc(cr.spec?.critique ?? cr.title)}”</div>
        ${(cr.spec?.attachments ?? []).map((k) => imagePreview({ uri: k, label: "Feedback attachment", alt: "Feedback attachment", esc, fileUrl, className: "refimg" })).join("")}
        ${cr.result?.plan ? `<div class="k">agent triage — ${esc(cr.result.changeType ?? "")}</div><div>${esc(cr.result.plan)}</div>` : ""}
        <div class="chain" data-chain="${cr.id}"><span class="muted">checking status…</span></div>
      </div>`;
    }
  }

  for (const t of d.tickets) {
    const spec = t.spec ?? {};
    const refKey = spec.referenceImageKey;
    const refUrl = spec.referenceImageUrl;
    html += `<h3>Ticket — what we asked for</h3><div class="card" data-ticket="${t.id}">
      <div class="row"><span class="k">title</span><b>${esc(t.title)}</b></div>
      <div class="row"><span class="k">type / status</span><span>${esc(t.type)} ${badge(t.status)}</span></div>
      <div class="row"><span class="k">pool / priority</span><span>${esc(t.pool)} / ${t.priority}</span></div>
      <div class="row"><span class="k">attempts</span><span>${t.attempt}/${t.maxAttempts}</span></div>
      <div class="row"><span class="k">budget</span><span class="cost">${money(t.spentUsd)} of ${money(t.budgetUsdCap)}</span></div>
      ${refKey ? `<div class="k">reference image</div>${imagePreview({ uri: refKey, label: "Reference image", alt: "Reference image", esc, fileUrl, className: "refimg" })}` : ""}
      ${refUrl ? `<div class="k">reference image (external)</div>${imagePreview({ uri: refUrl, label: "Reference image", alt: "External reference image", esc, fileUrl, className: "refimg" })}` : ""}
      <details><summary>spec — the exact input</summary>${json(spec)}</details>
      ${t.result ? `<details><summary>result — what the worker submitted</summary>${json(t.result)}</details>` : ""}
      <div class="actions">
        ${t.status === "submitted" ? `<button data-act="review" data-id="${t.id}">Send to review</button>` : ""}
        ${t.status === "review" ? `<button class="approve" data-act="approved" data-id="${t.id}">Approve</button>
                                   <button class="reject" data-act="rejected" data-id="${t.id}">Reject</button>` : ""}
      </div>
    </div>`;
  }

  if (d.runs.length) {
    html += `<h3>Runs — what the agent did</h3>`;
    for (const r of d.runs) {
      const secs = r.finishedAt ? ((new Date(r.finishedAt) - new Date(r.startedAt)) / 1000).toFixed(0) : "…";
      html += `<div class="card">
        <div class="row"><span class="k">${esc(r.agentRole)}</span>${badge(r.status)}</div>
        <div class="row"><span class="k">model</span><span>${esc(r.model)}</span></div>
        <div class="row"><span class="k">duration</span><span>${secs}s · ${when(r.startedAt)}</span></div>
        <div class="row"><span class="k">cost</span><span class="cost">${money(r.costUsd)}</span></div>
        ${r.rationale ? `<div class="k">rationale</div><div>${esc(r.rationale)}</div>` : ""}
        <details><summary>context slice — what it was given</summary>${json(r.contextSlice)}</details>
        <details><summary>outputs — what came back</summary>${json(r.outputs)}</details>
      </div>`;
    }
  }

  if (d.aiCalls.length) {
    html += `<h3>AI calls</h3>`;
    for (const c of d.aiCalls) {
      html += `<div class="card"><div class="row"><span class="k">${esc(c.agentRole)}</span>${badge(c.status)}</div>
        <div class="row"><span class="k">${esc(c.servedModel ?? c.model)}</span>
        <span>${c.promptTokens}→${c.completionTokens} tok · <span class="cost">${money(c.costUsd)}</span> · ${c.latencyMs}ms</span></div></div>`;
    }
  }

  if (d.artifacts.length) {
    html += `<h3>Artifacts</h3>`;
    for (const a of d.artifacts) {
      const isImg = /\.(png|jpe?g|webp)$/.test(a.uri);
      html += `<div class="card">
        <div class="row"><span class="k">${esc(a.kind)}</span><span>${when(a.createdAt)}</span></div>
        ${artifactLink({ uri: a.uri, label: a.uri, esc, fileUrl })}
        ${isImg ? imagePreview({ uri: a.uri, label: a.kind || "Artifact image", alt: a.kind || "Artifact image", esc, fileUrl, className: "refimg" }) : ""}
        ${Object.keys(a.meta ?? {}).length ? `<details><summary>meta</summary>${json(a.meta)}</details>` : ""}
      </div>`;
    }
  }

  if (d.events.length) {
    html += `<h3>Timeline</h3><ul class="timeline">`;
    for (const e of d.events) {
      html += `<li><span class="t">${when(e.at)}</span> ${e.fromStatus ? `${badge(e.fromStatus)}→` : ""}${badge(e.toStatus)}
        <span class="muted">${esc(e.actor)}${e.note ? ` · ${esc(e.note)}` : ""}</span></li>`;
    }
    html += `</ul>`;
  }
  return html;
}

/* version compare: toggle meshes, camera stays put */
let compare = null; // { list: [{v, key}], idx }
const compareBtn = document.createElement("button");
compareBtn.id = "compare-toggle";
compareBtn.style.display = "none";
$("#viewport").appendChild(compareBtn);
compareBtn.onclick = async () => {
  if (!compare) return;
  compare.idx = (compare.idx + 1) % compare.list.length;
  const cur = compare.list[compare.idx];
  try {
    currentStats = await viewer.load(fileUrl(cur.key), { keepCamera: true });
    renderStats();
    updateCompareBtn();
  } catch (err) { toast(String(err), true); }
};
function updateCompareBtn() {
  if (!compare) { compareBtn.style.display = "none"; return; }
  const cur = compare.list[compare.idx];
  const next = compare.list[(compare.idx + 1) % compare.list.length];
  const tag = (x) => (x.v === Math.max(...compare.list.map((y) => y.v)) ? "new" : "old");
  compareBtn.textContent = `showing v${cur.v} (${tag(cur)}) — click for v${next.v} (${tag(next)})`;
  compareBtn.style.display = "";
}
function endCompare() { compare = null; updateCompareBtn(); }

function bindActions(body) {
  body.querySelectorAll("[data-review-image]").forEach((button) => {
    button.onclick = () => showReviewImage(button.dataset.reviewImage, button.dataset.reviewImageLabel || "Review image");
  });
  body.querySelector("[data-asset-review-back]")?.addEventListener("click", () => {
    activeAssetReview = null;
    currentTab = "pipeline";
    updateDetailTabs();
    navigateView("inbox");
  });
  body.querySelector("[data-review-retry]")?.addEventListener("click", () => {
    if (currentDetail?.asset?.assetUid) selectAsset(currentDetail.asset.assetUid, { preserveReview: true });
  });
  body.querySelector("[data-review-inspect-model]")?.addEventListener("click", () => {
    closeMobileInspector();
    $("#viewport")?.focus?.();
  });
  body.querySelectorAll("[data-asset-review-action]").forEach((button) => {
    button.onclick = async () => {
      const to = button.dataset.assetReviewAction;
      if (to === "approved" && (!assetEvidenceLoaded || assetEvidenceError)) {
        toast("Load and inspect the submitted mesh before approving it.", true);
        return;
      }
      const note = body.querySelector("#asset-review-note")?.value.trim() || "";
      if (to === "rejected" && !note) {
        toast("Describe what should change before requesting a revision", true);
        body.querySelector("#asset-review-note")?.focus();
        return;
      }
      const ticket = activeAssetReview?.ticket;
      if (!ticket) return;
      button.disabled = true;
      button.classList.add("working");
      try {
        await api(`/tickets/${ticket.id}/transition`, {
          method: "POST",
          body: JSON.stringify({ to, actor: "human", note: note || "Approved in asset workspace" }),
        });
        toast(`${currentDetail?.asset?.displayName || ticket.title || "Asset"} → ${to === "approved" ? "approved" : "changes requested"}`);
        activeAssetReview = null;
        currentTab = "pipeline";
        updateDetailTabs();
        const reviewedAssetUid = currentDetail.asset.assetUid;
        await Promise.all([selectAsset(reviewedAssetUid), reviews.refreshCount()]);
        await navigateRoute("asset", { assetUid: reviewedAssetUid, assetName: currentDetail?.asset?.displayName, state: { tab: "pipeline" } }, { replace: true, focus: false });
      } catch (err) {
        toast(String(err), true);
        button.disabled = false;
        button.classList.remove("working");
      }
    };
  });
  body.querySelectorAll("[data-compare]").forEach((b) => {
    b.onclick = async () => {
      const list = JSON.parse(b.dataset.compare).sort((a, x) => x.v - a.v); // newest first
      compare = { list, idx: 0 };
      try {
        currentStats = await viewer.load(fileUrl(list[0].key), { keepCamera: false });
        renderStats();
        updateCompareBtn();
        toast("compare mode: camera stays fixed while you toggle versions");
      } catch (err) { toast(String(err), true); }
    };
  });
  body.querySelectorAll("[data-load-glb]").forEach((b) => {
    b.onclick = async () => {
      try {
        currentStats = await viewer.load(fileUrl(b.dataset.loadGlb), { keepCamera: true });
        renderStats();
        toast(`showing v${b.dataset.vnum}`);
      } catch (err) { toast(String(err), true); }
    };
  });
  body.querySelectorAll("[data-act]").forEach((b) => {
    b.onclick = async () => {
      const to = b.dataset.act === "review" ? "review" : b.dataset.act;
      const actor = to === "review" ? "coordinator" : "human";
      try {
        await api(`/tickets/${b.dataset.id}/transition`, {
          method: "POST",
          body: JSON.stringify({ to, actor, note: `via asset viewer` }),
        });
        toast(`ticket → ${to}`);
        selectAsset(currentDetail.asset.assetUid);
      } catch (err) {
        toast(String(err), true);
      }
    };
  });
}

function renderTextures(body) {
  const texs = viewer.getTextures();
  const fileTexs = (currentDetail?.artifacts ?? []).filter((a) => a.kind === "texture");
  if (!texs.length && !fileTexs.length) {
    body.innerHTML = `<p class="muted">No textures on the loaded model.</p>`;
    return;
  }
  body.innerHTML = `<h3>Embedded in GLB</h3><div class="tex-grid" id="tex-embedded"></div>
    ${fileTexs.length ? `<h3>Texture files (R2)</h3><div class="tex-grid">${fileTexs
      .map((a) => `<div class="tex-cell"><img src="${esc(fileUrl(a.uri))}" loading="lazy" alt="${esc(a.meta?.map ?? a.uri.split("/").pop())}" /><div>${esc(a.meta?.map ?? a.uri.split("/").pop())}</div></div>`)
      .join("")}</div>` : ""}`;
  const grid = body.querySelector("#tex-embedded");
  for (const { label, image } of texs) {
    const cell = document.createElement("div");
    cell.className = "tex-cell";
    const cv = document.createElement("canvas");
    const w = Math.min(image.width ?? 256, 512);
    cv.width = w;
    cv.height = w * ((image.height ?? 256) / (image.width ?? 256));
    cv.getContext("2d").drawImage(image, 0, 0, cv.width, cv.height);
    cell.appendChild(cv);
    cell.insertAdjacentHTML("beforeend", `<div>${esc(label)} · ${image.width}×${image.height}</div>`);
    grid.appendChild(cell);
  }
}

async function bootstrap() {
  switchView("studio");
  const requested = parseRoute(location);
  applyingRoute = true;
  try {
    await studio.refresh(requested.projectId || "");
  } finally {
    applyingRoute = false;
  }
  let route = requested;
  if (!route.projectId && studio.getProjectId() && !route.legacy) {
    history.replaceState({}, "", projectRoute("studio"));
    route = parseRoute(location);
  }
  await applyRoute(route);
}

addEventListener("popstate", () => applyRoute(parseRoute(location), { focus: true }));
bootstrap().catch((err) => toast(String(err), true));
